import { mergeAccountBranding } from '../utils/accountBranding';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  CircularProgress,
  IconButton,
  LinearProgress,
  Alert,
} from '@mui/material';
import { toast } from 'react-toastify';
import { agencyApi } from '../services/api';
import { environment } from '../env/environment';
import logger from '../utils/logger';

// Color definitions for each account type
const ACCOUNT_TYPES = {
  client: {
    name: 'Client',
    fullName: 'Client Portal',
    desc: 'Patient-facing intake & appointment portal.',
    color: '#378ADD',
    bg: '#E6F1FB',
    fg: '#185FA5',
    chipBg: 'rgba(55,138,221,.13)',
  },
  admin: {
    name: 'Admin',
    fullName: 'Admin Dashboard',
    desc: 'Staff dashboard for records & scheduling.',
    color: '#7F77DD',
    bg: '#EEEDFE',
    fg: '#3C3489',
    chipBg: 'rgba(127,119,221,.13)',
  },
  superadmin: {
    name: 'SuperAdmin',
    fullName: 'SuperAdmin Panel',
    desc: 'Full platform control, billing & audit.',
    color: '#EF9F27',
    bg: '#FAEEDA',
    fg: '#633806',
    chipBg: 'rgba(239,159,39,.13)',
  },
  followup: {
    name: 'Followup',
    fullName: 'Followup System',
    desc: 'Automated reminders & follow-ups.',
    color: '#639922',
    bg: '#EAF3DE',
    fg: '#27500A',
    chipBg: 'rgba(99,153,34,.13)',
  },
  kiosk: {
    name: 'Kiosk',
    fullName: 'Kiosk App',
    desc: 'In-office tablet intake for walk-in patients.',
    color: '#E91E63',
    bg: '#FCE4EC',
    fg: '#880E4F',
    chipBg: 'rgba(233,30,99,.13)',
  },
};

// DNS targets - fetched from Sevalla API, with kiosk fallback (static site, not in applications API)
let DNS_TARGETS = {
  client: null,
  admin: null,
  superadmin: null,
  followup: null,
  kiosk: 'patient-kiosk-g3lrg.kinsta.page',
};

// Icons
const Icons = {
  back: (
    <svg viewBox="0 0 13 13" fill="none" stroke="currentColor" style={{ width: 13, height: 13 }}>
      <path d="M8 2L3 6.5 8 11" />
    </svg>
  ),
  grid: (
    <svg viewBox="0 0 13 13" fill="none" stroke="currentColor" style={{ width: 13, height: 13 }}>
      <rect x="1" y="1" width="4.5" height="4.5" rx="1" />
      <rect x="7.5" y="1" width="4.5" height="4.5" rx="1" />
      <rect x="1" y="7.5" width="4.5" height="4.5" rx="1" />
      <rect x="7.5" y="7.5" width="4.5" height="4.5" rx="1" />
    </svg>
  ),
  chevron: (
    <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" style={{ width: 12, height: 12 }}>
      <path d="M3 4.5l3 3 3-3" />
    </svg>
  ),
  client: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20c0-4.4 3.6-8 8-8s8 3.6 8 8" />
    </svg>
  ),
  admin: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M12 3l3 6.5 7 1-5 4.8 1.2 7L12 19l-6.2 3.3L7 15.3 2 10.5l7-1z" />
    </svg>
  ),
  superadmin: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="12" cy="9" r="4" />
      <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
      <circle cx="12" cy="9" r="9" strokeDasharray="3 2" />
    </svg>
  ),
  followup: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="3" y="7" width="18" height="14" rx="2" />
      <path d="M8 7V5M16 7V5" />
      <path d="M7 13h10M7 17h6" />
    </svg>
  ),
  kiosk: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <path d="M4 18h16" />
      <circle cx="12" cy="20" r="1" fill="currentColor" stroke="none" />
    </svg>
  ),
  branding: (
    <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.7">
      <rect x="1" y="1" width="12" height="9" rx="1.5" />
      <circle cx="4.5" cy="4.5" r="1.5" />
      <path d="M1 8l3-3 3 3 2.5-2.5L13 9" />
    </svg>
  ),
  domain: (
    <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.7">
      <circle cx="7" cy="7" r="5.5" />
      <path d="M7 1c-2.5 2-2.5 10 0 12M7 1c2.5 2 2.5 10 0 12M1.5 7h11" />
    </svg>
  ),
  permissions: (
    <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.7">
      <rect x="3" y="6" width="8" height="6" rx="1.5" />
      <path d="M5 6V4.5a2 2 0 014 0V6" />
    </svg>
  ),
  webhook: (
    <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.7">
      <circle cx="7" cy="7" r="2" />
      <path d="M7 1v2M7 11v2M1 7h2M11 7h2M2.5 2.5l1.5 1.5M10 10l1.5 1.5M2.5 11.5l1.5-1.5M10 4l1.5-1.5" />
    </svg>
  ),
  external: (
    <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.2" style={{ width: 11, height: 11 }}>
      <path d="M2 10L10 2M10 2H5M10 2v5" />
    </svg>
  ),
  copy: (
    <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ width: 14, height: 14 }}>
      <rect x="4" y="4" width="8" height="8" rx="1.5" />
      <path d="M4 10H3a1.5 1.5 0 01-1.5-1.5V3A1.5 1.5 0 013 1.5h5.5A1.5 1.5 0 0110 3v1" />
    </svg>
  ),
  check: (
    <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 14, height: 14 }}>
      <path d="M3 7l3 3 5-5" />
    </svg>
  ),
  googledrive: (
    <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ width: 14, height: 14 }}>
      <path d="M1.5 9.5L4.5 4l3 5.5H1.5z" />
      <path d="M4.5 4l3 5.5h5L9.5 4H4.5z" />
      <path d="M7.5 9.5h5l-2 3.5H5.5l2-3.5z" />
    </svg>
  ),
  upload: (
    <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ width: 14, height: 14 }}>
      <path d="M7 10V3M7 3L4 6M7 3l3 3" />
      <path d="M2 11v1.5a1 1 0 001 1h8a1 1 0 001-1V11" />
    </svg>
  ),
  trash: (
    <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ width: 14, height: 14 }}>
      <path d="M2 4h10M5 4V2.5a1 1 0 011-1h2a1 1 0 011 1V4M11 4v8a1 1 0 01-1 1H4a1 1 0 01-1-1V4" />
    </svg>
  ),
};

const EditSubaccount = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const logoInputRef = useRef(null);
  const faviconInputRef = useRef(null);

  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [subaccount, setSubaccount] = useState(null);

  // Navigation state
  const [currentType, setCurrentType] = useState(null); // null = overview, or 'client'/'admin'/etc
  const [currentPage, setCurrentPage] = useState('overview'); // 'overview', 'branding', 'domain', 'permissions'
  const [openSections, setOpenSections] = useState(new Set());

  // Button colors inherit app branding until explicitly customized.
  const defaultButtonStyles = {};
  const defaultSuperAdminButtonStyles = {};

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    brandName: '',
    logoUrl: '',
    signinLogoUrl: '', // Separate logo for signin pages (white/light logo)
    faviconUrl: '',
    // Global branding (fallback)
    primaryColor: '#06AB8B',
    secondaryColor: '#049572',
    buttonHoverColor: '',
    buttonBoxShadow: '',
    // Per-account-type branding
    accountBranding: {
      client: { primaryColor: '#378ADD', secondaryColor: '#185FA5', buttonHoverColor: '', buttonBoxShadow: '' },
      admin: { primaryColor: '#06AB8B', secondaryColor: '#049572', buttonHoverColor: '', buttonBoxShadow: '', buttonStyles: defaultButtonStyles },
      superadmin: { primaryColor: '#EF9F27', secondaryColor: '#633806', buttonHoverColor: '', buttonBoxShadow: '', buttonStyles: defaultSuperAdminButtonStyles },
      followup: { primaryColor: '#639922', secondaryColor: '#27500A', buttonHoverColor: '', buttonBoxShadow: '' },
      kiosk: { primaryColor: '#E91E63', secondaryColor: '#880E4F', buttonHoverColor: '', buttonBoxShadow: '' },
    },
    clientDomain: '',
    adminDomain: '',
    superadminDomain: '',
    followupDomain: '',
    kioskDomain: '',
    isActive: true,
    features: {
      pain_diagram: true,
      followup_enabled: true,
      pdf_generation: true,
      google_drive_upload: true,
    },
  });

  // Logo confirmation dialog state
  const [logoConfirmDialog, setLogoConfirmDialog] = useState({
    open: false,
    file: null,
    type: null, // 'logo' or 'signinLogo'
  });
  const [useSeparateSigninLogo, setUseSeparateSigninLogo] = useState(false);
  const signinLogoInputRef = useRef(null);

  // Domain verification state
  const [verifyingDomain, setVerifyingDomain] = useState({});
  const [domainStatus, setDomainStatus] = useState({});
  const [sevallaDnsRecords, setSevallaDnsRecords] = useState({}); // DNS records from Sevalla
  const [domainPrimaryStatus, setDomainPrimaryStatus] = useState({}); // Is domain primary
  const [makingPrimary, setMakingPrimary] = useState({}); // Loading state for make primary
  const [refreshingDns, setRefreshingDns] = useState({}); // Loading state for refreshing DNS records
  const [dnsTargets, setDnsTargets] = useState(DNS_TARGETS); // Sevalla default domains

  // Per-account colors (editable) - should match formData.accountBranding defaults
  const [, setAccountColors] = useState({
    client: '#378ADD',
    admin: '#06AB8B',
    superadmin: '#EF9F27',
    followup: '#639922',
    kiosk: '#E91E63',
  });

  // Webhooks state (intake form + follow-up form)
  const [webhooks, setWebhooks] = useState({
    step1: '',
    step4: '',
    final: '',
    // Follow-up webhooks
    followup_step1: '',
    followup_submit: '',
  });
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [webhookMessage, setWebhookMessage] = useState('');

  // Google Drive state
  const [googleDriveConfig, setGoogleDriveConfig] = useState({
    folderId: '',
    hasCredentials: false,
    projectId: '',
    clientEmail: '',
  });
  const [googleDriveLoading, setGoogleDriveLoading] = useState(false);
  const [googleDriveUploading, setGoogleDriveUploading] = useState(false);
  const googleDriveJsonRef = useRef(null);

  useEffect(() => {
    fetchSubaccount();
    fetchDomainStatus();
    fetchWebhooks();
    fetchSevallaDefaultDomains();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Fetch default Sevalla domains (.kinsta.page)
  const fetchSevallaDefaultDomains = async () => {
    try {
      const response = await agencyApi.getSevallaDefaultDomains();
      if (response.data.success && response.data.domains) {
        const domains = response.data.domains;
        DNS_TARGETS = { ...DNS_TARGETS, ...domains };
        setDnsTargets(prev => ({ ...prev, ...domains }));
      }
    } catch (err) {
      logger.error('Error fetching Sevalla default domains:', err);
    }
  };

  // Fetch Google Drive config when googledrive page is opened
  useEffect(() => {
    if (currentPage === 'googledrive' && currentType === 'client') {
      fetchGoogleDriveConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, currentType]);

  // Fetch Sevalla DNS records and primary status when domain page is opened
  useEffect(() => {
    if (currentPage === 'domain' && currentType) {
      const domainKey = `${currentType}Domain`;
      const domain = formData[domainKey];
      if (domain) {
        fetchSevallaDnsRecords(currentType);
        fetchDomainPrimaryStatus(currentType);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, currentType, formData.clientDomain, formData.adminDomain, formData.superadminDomain, formData.followupDomain, formData.kioskDomain]);

  // Fetch primary status for a domain
  const fetchDomainPrimaryStatus = async (domainType) => {
    try {
      const response = await agencyApi.checkDomainPrimary(id, domainType);
      if (response.data.success) {
        setDomainPrimaryStatus(prev => ({
          ...prev,
          [domainType]: response.data.isPrimary,
        }));
      }
    } catch (err) {
      logger.error('Error fetching domain primary status:', err);
    }
  };

  // Make domain primary
  const handleMakePrimary = async (domainType) => {
    setMakingPrimary(prev => ({ ...prev, [domainType]: true }));
    try {
      const response = await agencyApi.makeDomainPrimary(id, domainType);
      if (response.data.success) {
        toast.success(response.data.message || 'Domain set as primary!');
        setDomainPrimaryStatus(prev => ({ ...prev, [domainType]: true }));
      } else {
        toast.error(response.data.message || 'Failed to make domain primary');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to make domain primary');
    } finally {
      setMakingPrimary(prev => ({ ...prev, [domainType]: false }));
    }
  };

  const fetchWebhooks = async () => {
    try {
      const response = await agencyApi.getWebhooks(id);
      if (response.data.webhooks) {
        setWebhooks(prev => ({ ...prev, ...response.data.webhooks }));
      }
    } catch (err) {
      logger.error('Error fetching webhooks:', err);
    }
  };

  const handleWebhookChange = (e) => {
    const { name, value } = e.target;
    setWebhooks(prev => ({ ...prev, [name]: value }));
  };

  const handleWebhookUpdate = async (step) => {
    setWebhookLoading(true);
    setWebhookMessage('');
    try {
      const response = await agencyApi.updateWebhook(id, step, webhooks[step]);
      if (response.data.message) {
        setWebhookMessage(response.data.message);
        toast.success('Webhook updated successfully!');
      }
    } catch (err) {
      setWebhookMessage('Error updating webhook');
      toast.error('Failed to update webhook');
    } finally {
      setWebhookLoading(false);
      setTimeout(() => setWebhookMessage(''), 3000);
    }
  };

  // Google Drive functions
  const fetchGoogleDriveConfig = async () => {
    try {
      const response = await agencyApi.getGoogleDriveConfig(id);
      if (response.data.success) {
        setGoogleDriveConfig(response.data.config);
      }
    } catch (err) {
      logger.error('Error fetching Google Drive config:', err);
    }
  };

  const handleGoogleDriveUpdate = async () => {
    if (!googleDriveConfig.folderId) {
      toast.error('Folder ID is required');
      return;
    }

    setGoogleDriveLoading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('folderId', googleDriveConfig.folderId);

      // If there's a file selected, add it
      const fileInput = googleDriveJsonRef.current;
      if (fileInput?.files?.[0]) {
        formDataUpload.append('jsonFile', fileInput.files[0]);
      }

      const response = await agencyApi.updateGoogleDriveConfig(id, formDataUpload);
      if (response.data.success) {
        toast.success(response.data.message || 'Google Drive configuration updated!');
        setGoogleDriveConfig(response.data.config);
        // Clear file input
        if (fileInput) fileInput.value = '';
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update Google Drive configuration');
    } finally {
      setGoogleDriveLoading(false);
    }
  };

  const handleGoogleDriveDelete = async () => {
    if (!window.confirm('Are you sure you want to remove Google Drive configuration?')) {
      return;
    }

    setGoogleDriveLoading(true);
    try {
      const response = await agencyApi.deleteGoogleDriveConfig(id);
      if (response.data.success) {
        toast.success(response.data.message || 'Google Drive configuration removed!');
        setGoogleDriveConfig({
          folderId: '',
          hasCredentials: false,
          projectId: '',
          clientEmail: '',
        });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove Google Drive configuration');
    } finally {
      setGoogleDriveLoading(false);
    }
  };

  const fetchSubaccount = async () => {
    try {
      const response = await agencyApi.getSubaccount(id);
      if (response.data.success) {
        const sub = response.data.subaccount;
        setSubaccount(sub);

        // Default account branding per account type
        const defaultAccountBranding = {
          client: { primaryColor: '#378ADD', secondaryColor: '#185FA5', buttonHoverColor: '', buttonBoxShadow: '' },
          admin: {
            primaryColor: '#06AB8B',
            secondaryColor: '#049572',
            buttonHoverColor: '',
            buttonBoxShadow: '',
            buttonStyles: {
              addModule: { bg: '#1e1e1e', text: '#ffffff', hover: '#474747' },
              import: { bg: '#06AB8B', text: '#ffffff', hover: '#049572' },
              rename: { bg: '#06AB8B', text: '#ffffff', hover: '#049572' },
              delete: { bg: '#dc3545', text: '#ffffff', hover: '#c82333' },
              export: { bg: 'transparent', text: '#333333', hover: '#f0f0f0' },
              save: { bg: '#06AB8B', text: '#ffffff', hover: '#049572' },
              signin: { bg: '#06AB8B', text: '#ffffff', hover: '#049572' },
            }
          },
          superadmin: {
            primaryColor: '#EF9F27',
            secondaryColor: '#633806',
            buttonHoverColor: '',
            buttonBoxShadow: '',
            buttonStyles: {
              signin: { bg: '#EF9F27', text: '#ffffff', hover: '#633806' },
              createAdmin: { bg: '#06ab8b', text: '#ffffff', hover: '#049572' },
              editAdmin: { bg: '#2196F3', text: '#ffffff', hover: '#1976D2' },
              deleteAdmin: { bg: '#f44336', text: '#ffffff', hover: '#d32f2f' },
            }
          },
          followup: { primaryColor: '#639922', secondaryColor: '#27500A', buttonHoverColor: '', buttonBoxShadow: '' },
          kiosk: { primaryColor: '#E91E63', secondaryColor: '#880E4F', buttonHoverColor: '', buttonBoxShadow: '' },
        };

        // Merge saved account branding with defaults
        const savedAccountBranding = sub.account_branding || {};
        const mergedAccountBranding = mergeAccountBranding(defaultAccountBranding, savedAccountBranding);

        setFormData({
          name: sub.name || '',
          brandName: sub.brand_name || '',
          logoUrl: sub.logo_url || '',
          signinLogoUrl: sub.signin_logo_url || '',
          faviconUrl: sub.favicon_url || '',
          primaryColor: sub.primary_color || '#06AB8B',
          secondaryColor: sub.secondary_color || '#049572',
          buttonHoverColor: sub.button_hover_color || '',
          buttonBoxShadow: sub.button_box_shadow || '',
          accountBranding: mergedAccountBranding,
          clientDomain: sub.client_domain || '',
          adminDomain: sub.admin_domain || '',
          superadminDomain: sub.superadmin_domain || '',
          followupDomain: sub.followup_domain || '',
          kioskDomain: sub.kiosk_domain || '',
          isActive: sub.is_active ?? true,
          features: sub.features || {
            pain_diagram: true,
            followup_enabled: true,
            pdf_generation: true,
            google_drive_upload: true,
          },
        });
        // Set useSeparateSigninLogo if signin logo exists
        setUseSeparateSigninLogo(!!sub.signin_logo_url);
        // Sync account colors from accountBranding
        setAccountColors({
          client: mergedAccountBranding.client.primaryColor,
          admin: mergedAccountBranding.admin.primaryColor,
          superadmin: mergedAccountBranding.superadmin.primaryColor,
          followup: mergedAccountBranding.followup.primaryColor,
          kiosk: mergedAccountBranding.kiosk.primaryColor,
        });
      }
    } catch (err) {
      toast.error('Error loading subaccount');
    } finally {
      setLoading(false);
    }
  };

  const fetchDomainStatus = async () => {
    try {
      const response = await agencyApi.getDomainStatus(id);
      if (response.data.success) {
        setDomainStatus(response.data.domains);
      }
    } catch (err) {
      logger.error('Error fetching domain status:', err);
    }
  };

  // Fetch DNS records from Sevalla for a specific domain type
  const fetchSevallaDnsRecords = async (domainType) => {
    try {
      const response = await agencyApi.getDnsInstructionsForDomain(id, domainType);
      if (response.data.success && response.data.dnsRecords) {
        setSevallaDnsRecords(prev => ({
          ...prev,
          [domainType]: {
            records: response.data.dnsRecords,
            fromSevalla: response.data.fromSevalla,
            sevallaStatus: response.data.sevallaStatus,
            verificationStatus: response.data.verificationStatus,
            sevallaMessage: response.data.sevallaMessage,
            sevallaAddResult: response.data.sevallaAddResult,
          }
        }));
      }
    } catch (err) {
      logger.error('Error fetching Sevalla DNS records:', err);
    }
  };

  // Refresh DNS records from Sevalla (with loading state)
  const handleRefreshDnsRecords = async (domainType) => {
    setRefreshingDns(prev => ({ ...prev, [domainType]: true }));
    try {
      await fetchSevallaDnsRecords(domainType);
      await fetchDomainPrimaryStatus(domainType);
      toast.success('DNS records refreshed from Sevalla');
    } catch (err) {
      toast.error('Failed to refresh DNS records');
    } finally {
      setRefreshingDns(prev => ({ ...prev, [domainType]: false }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData = {
        name: formData.name,
        brandName: formData.brandName,
        // Only include logoUrl/faviconUrl if they have a value, otherwise use undefined to preserve existing
        logoUrl: formData.logoUrl || undefined,
        signinLogoUrl: useSeparateSigninLogo ? (formData.signinLogoUrl || undefined) : undefined,
        faviconUrl: formData.faviconUrl || undefined,
        primaryColor: formData.primaryColor,
        secondaryColor: formData.secondaryColor,
        buttonHoverColor: formData.buttonHoverColor || null,
        buttonBoxShadow: formData.buttonBoxShadow || null,
        accountBranding: formData.accountBranding,
        clientDomain: formData.clientDomain || null,
        adminDomain: formData.adminDomain || null,
        superadminDomain: formData.superadminDomain || null,
        followupDomain: formData.followupDomain || null,
        kioskDomain: formData.kioskDomain || null,
        isActive: formData.isActive,
        features: formData.features,
      };

      const response = await agencyApi.updateSubaccount(id, updateData);
      if (response.data.success) {
        toast.success('Changes saved successfully!');
        setSubaccount(response.data.subaccount);

        // Re-fetch DNS records from Sevalla after saving (domains are added to Sevalla on save)
        // Wait a moment for Sevalla to process the domain addition
        if (currentPage === 'domain' && currentType) {
          setTimeout(async () => {
            await fetchSevallaDnsRecords(currentType);
            await fetchDomainPrimaryStatus(currentType);
            toast.info('DNS records refreshed from Sevalla');
          }, 2000);
        }
      }
    } catch (err) {
      toast.error('Error saving changes');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml', 'image/x-icon'];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file type');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large (max 5MB)');
      return;
    }

    // For logo upload, show confirmation dialog asking about separate signin logo
    if (type === 'logo' && !useSeparateSigninLogo) {
      setLogoConfirmDialog({ open: true, file, type: 'logo' });
      return;
    }

    await uploadFile(file, type);
  };

  // Actual file upload function
  //
  // `appType` is only used by the account_branding uploads (qrCode/metaImage)
  // to say which branding section the image belongs to.
  const uploadFile = async (file, type, appType, options = {}) => {
    setUploading(true);
    try {
      const formDataUpload = new FormData();
      // Map signinLogo to logo for API (same endpoint, different field name)
      const apiType = type === 'signinLogo' ? 'signinLogo' : type;
      formDataUpload.append(apiType, file);
      if (appType) formDataUpload.append('appType', appType);
      const response = await agencyApi.uploadBranding(id, formDataUpload);

      if (response.data.success) {
        // Sync from the saved tenant the server echoes back, so form state
        // holds the real stored image. This used to read response.data.urls,
        // which was the literal string '(base64 stored)' — that placeholder
        // went into state and the next Save wrote it over the real image.
        const saved = response.data.subaccount || {};
        if (type === 'logo' && saved.logo_url) {
          setFormData((prev) => ({ ...prev, logoUrl: saved.logo_url }));
        }
        if (type === 'signinLogo' && saved.signin_logo_url) {
          setFormData((prev) => ({ ...prev, signinLogoUrl: saved.signin_logo_url }));
        }
        if (type === 'favicon' && saved.favicon_url) {
          setFormData((prev) => ({ ...prev, faviconUrl: saved.favicon_url }));
        }
        if ((type === 'qrCode' || type === 'metaImage') && saved.account_branding) {
          setFormData((prev) => ({ ...prev, accountBranding: saved.account_branding }));
        }
        const label =
          type === 'signinLogo' ? 'Signin logo' : type === 'qrCode' ? 'QR code' : type;
        toast.success(`${label} uploaded!`);
        return true;
      }
      return false;
    } catch (err) {
      // qrCode/metaImage are only accepted by a server new enough to declare
      // those multipart fields; an older one makes multer reject the whole
      // request as an unexpected field (500). Callers that can degrade pass
      // allowFallback so they can handle it instead of showing an error.
      if (!options.allowFallback) toast.error('Upload failed');
      return false;
    } finally {
      setUploading(false);
    }
  };

  /**
   * Upload an account_branding image (QR code, meta image).
   *
   * Prefers the multipart upload endpoint, which stores the image on its own
   * and keeps the base64 off the Save payload entirely. Against a server that
   * doesn't accept those fields yet, fall back to carrying it in form state so
   * the picker still works — on that older server Save will resubmit it and
   * the input sanitizer will truncate it, which is the bug the upload path
   * exists to avoid.
   */
  const uploadBrandingImage = async (file, field, appType) => {
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large (max 5MB)');
      return;
    }

    const uploaded = await uploadFile(file, field, appType, { allowFallback: true });
    if (uploaded) return;

    const key = field === 'qrCode' ? 'qrCodeUrl' : 'metaImage';
    const reader = new FileReader();
    reader.onload = (event) => {
      setFormData((prev) => ({
        ...prev,
        accountBranding: {
          ...prev.accountBranding,
          [appType]: { ...prev.accountBranding?.[appType], [key]: event.target.result },
        },
      }));
      toast.success('Image added — click Save to apply.');
    };
    reader.onerror = () => toast.error('Failed to read image');
    reader.readAsDataURL(file);
  };

  // Handle logo confirmation dialog response
  const handleLogoConfirmResponse = async (useSameLogo) => {
    const { file } = logoConfirmDialog;
    setLogoConfirmDialog({ open: false, file: null, type: null });

    if (useSameLogo) {
      // User wants same logo for both - just upload as logo
      setUseSeparateSigninLogo(false);
      await uploadFile(file, 'logo');
    } else {
      // User wants different logos - enable separate signin logo mode and upload this as dashboard logo
      setUseSeparateSigninLogo(true);
      await uploadFile(file, 'logo');
      toast.info('Now upload a separate logo for the signin page below');
    }
  };

  const handleVerifyDomain = async (type) => {
    const domainKey = `${type}Domain`;
    const domain = formData[domainKey];
    if (!domain) return;

    setVerifyingDomain((prev) => ({ ...prev, [type]: true }));
    try {
      const response = await agencyApi.verifyDomain(id, type, domain);
      if (response.data.verified) {
        toast.success('Domain verified!');
        setDomainStatus((prev) => ({
          ...prev,
          [type]: { ...prev[type], verified: true },
        }));
      } else {
        toast.warning(response.data.message || 'DNS not configured yet');
      }
    } catch (err) {
      toast.error('Verification failed');
    } finally {
      setVerifyingDomain((prev) => ({ ...prev, [type]: false }));
    }
  };

  const toggleSection = (type) => {
    setOpenSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(type)) {
        newSet.delete(type);
      } else {
        newSet.add(type);
      }
      return newSet;
    });
  };

  const navigateTo = (type, page) => {
    setCurrentType(type);
    setCurrentPage(page);
    if (type && !openSections.has(type)) {
      setOpenSections((prev) => new Set([...prev, type]));
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied!');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!subaccount) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography>Subaccount not found</Typography>
        <Button onClick={() => navigate('/subaccounts')} sx={{ mt: 2 }}>
          Back
        </Button>
      </Box>
    );
  }

  // Render functions for different pages
  const renderMainOverview = () => (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Typography sx={{ fontSize: 28, fontWeight: 600, letterSpacing: '-0.5px', color: '#151918' }}>
            {subaccount.name}
          </Typography>
          <Typography sx={{ fontSize: 15, color: '#8A9A94', mt: 1 }}>
            Agency subaccount &nbsp;·&nbsp; {subaccount.slug}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Box sx={{ px: 2, py: 0.6, borderRadius: 20, fontSize: 13, fontWeight: 600, bgcolor: formData.isActive ? '#1D9E75' : '#ccc', color: '#fff' }}>
              {formData.isActive ? 'Active' : 'Inactive'}
            </Box>
          </Box>
        </Box>
        <Button
          variant="outlined"
          onClick={handleSave}
          disabled={saving}
          sx={{ fontSize: 14, textTransform: 'none', borderColor: 'rgba(0,0,0,.13)', color: '#4A5550', px: 3, py: 1 }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>

      {/* Stats Grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 4 }}>
        <Box sx={{ bgcolor: '#fff', border: '1px solid rgba(0,0,0,.08)', borderRadius: 2, p: 3 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.07em', color: '#8A9A94', mb: 1 }}>Slug</Typography>
          <Typography sx={{ fontSize: 15, fontFamily: 'monospace', color: '#151918' }}>{subaccount.slug}</Typography>
        </Box>
        <Box sx={{ bgcolor: '#fff', border: '1px solid rgba(0,0,0,.08)', borderRadius: 2, p: 3 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.07em', color: '#8A9A94', mb: 1 }}>Applications</Typography>
          <Typography sx={{ fontSize: 24, fontWeight: 600, color: '#151918' }}>5</Typography>
        </Box>
      </Box>

      {/* Account Types Label */}
      <Typography sx={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.07em', color: '#8A9A94', mb: 2 }}>
        Account applications — click to manage
      </Typography>

      {/* Account Type Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        {Object.entries(ACCOUNT_TYPES).map(([key, type]) => (
          <Box
            key={key}
            onClick={() => navigateTo(key, 'overview')}
            sx={{
              bgcolor: type.bg,
              borderRadius: 2.5,
              p: 3,
              cursor: 'pointer',
              border: '2px solid transparent',
              transition: 'all .15s',
              '&:hover': {
                transform: 'translateY(-3px)',
                borderColor: `${type.color}40`,
                boxShadow: '0 8px 25px rgba(0,0,0,.08)',
              },
            }}
          >
            <Box
              sx={{
                width: 50,
                height: 50,
                borderRadius: 1.5,
                bgcolor: `${type.color}22`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
                '& svg': { width: 26, height: 26, stroke: type.color },
              }}
            >
              {Icons[key]}
            </Box>
            <Typography sx={{ fontSize: 16, fontWeight: 600, color: type.fg, mb: 0.5 }}>{type.fullName}</Typography>
            <Typography sx={{ fontSize: 14, color: type.fg, opacity: 0.72, lineHeight: 1.5 }}>{type.desc}</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
              <Box sx={{ fontSize: 12, fontWeight: 600, px: 1.5, py: 0.5, borderRadius: 20, bgcolor: 'rgba(0,0,0,.07)', color: type.fg }}>
                Active
              </Box>
              <Typography sx={{ fontSize: 22, opacity: 0.5, color: type.fg }}>›</Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );

  const renderTypeOverview = (type) => {
    const config = ACCOUNT_TYPES[type];
    const domainKey = `${type}Domain`;
    const domain = formData[domainKey];

    return (
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
          <Box>
            <Typography sx={{ fontSize: 28, fontWeight: 600, letterSpacing: '-0.5px', color: '#151918' }}>
              {config.fullName}
            </Typography>
            <Typography sx={{ fontSize: 15, color: '#8A9A94', mt: 1 }}>{config.desc}</Typography>
          </Box>
          <Button
            sx={{
              bgcolor: config.fg,
              color: '#fff',
              fontSize: 14,
              fontWeight: 600,
              textTransform: 'none',
              px: 3,
              py: 1.2,
              '&:hover': { bgcolor: config.fg, opacity: 0.85 },
            }}
          >
            Open app {Icons.external}
          </Button>
        </Box>

        {/* Hero */}
        <Box
          sx={{
            bgcolor: config.bg,
            borderRadius: 2.5,
            p: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2.5,
            mb: 4,
          }}
        >
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: 2,
              bgcolor: `${config.color}22`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '& svg': { width: 32, height: 32, stroke: config.color },
            }}
          >
            {Icons[type]}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 20, fontWeight: 600, color: config.fg, letterSpacing: '-0.3px' }}>
              {config.fullName}
            </Typography>
            <Typography sx={{ fontSize: 14, color: config.fg, opacity: 0.72, mt: 0.5 }}>
              {domain || `${environment.baseUrl}/share/${type}/${subaccount.slug}`}
            </Typography>
          </Box>
          <Box sx={{ px: 2, py: 0.8, borderRadius: 20, fontSize: 13, fontWeight: 600, bgcolor: config.chipBg, color: config.fg }}>
            Active
          </Box>
        </Box>

        {/* Quick Nav */}
        <Typography sx={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.07em', color: '#8A9A94', mb: 2 }}>
          Configure {config.name}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          {[
            { key: 'branding', name: 'Branding', sub: 'Logo, favicon, colours', icon: Icons.branding, show: true },
            { key: 'domain', name: 'Custom Domain', sub: 'DNS & domain setup', icon: Icons.domain, show: true },
            { key: 'permissions', name: 'Permissions', sub: 'Feature toggles', icon: Icons.permissions, show: type === 'client' },
            { key: 'webhooks', name: 'Webhooks', sub: 'Form submission webhooks', icon: Icons.webhook, show: true },
          ].filter(item => item.show).map((item) => (
            <Box
              key={item.key}
              onClick={() => navigateTo(type, item.key)}
              sx={{
                bgcolor: '#fff',
                border: '1px solid rgba(0,0,0,.08)',
                borderRadius: 2,
                p: 2.5,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                transition: 'all .15s',
                '&:hover': { borderColor: '#1D9E75', transform: 'translateY(-2px)', boxShadow: '0 4px 15px rgba(0,0,0,.06)' },
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 1.5,
                  bgcolor: '#F2F4F3',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '& svg': { width: 20, height: 20, stroke: '#4A5550' },
                }}
              >
                {item.icon}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#151918' }}>{item.name}</Typography>
                <Typography sx={{ fontSize: 13, color: '#8A9A94', mt: 0.3 }}>{item.sub}</Typography>
              </Box>
              <Typography sx={{ color: '#8A9A94', fontSize: 20 }}>›</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const renderBranding = (type) => {
    const config = ACCOUNT_TYPES[type];

    return (
      <Box>
        <Box sx={{ mb: 4 }}>
          <Typography sx={{ fontSize: 28, fontWeight: 600, color: '#151918' }}>Branding</Typography>
          <Typography sx={{ fontSize: 15, color: '#8A9A94', mt: 1 }}>Customise the visual identity for the {config.fullName}.</Typography>
        </Box>

        {uploading && <LinearProgress sx={{ mb: 3 }} />}

        {/* Logo Confirmation Dialog - Not shown for client type */}
        {logoConfirmDialog.open && type !== 'client' && (
          <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
            <Box sx={{ bgcolor: '#fff', borderRadius: 3, p: 4, maxWidth: 450, width: '90%', boxShadow: '0 20px 50px rgba(0,0,0,0.2)' }}>
              <Typography sx={{ fontSize: 20, fontWeight: 600, color: '#151918', mb: 2 }}>Logo Configuration</Typography>
              <Typography sx={{ fontSize: 15, color: '#4A5550', mb: 3, lineHeight: 1.6 }}>
                Do you want to use the same logo for both the <strong>signin page</strong> and <strong>dashboard</strong>, or upload a separate logo for the signin page?
              </Typography>
              <Typography sx={{ fontSize: 13, color: '#8A9A94', mb: 3, p: 2, bgcolor: '#f8f9fa', borderRadius: 1.5 }}>
                <strong>Tip:</strong> Use a white/light logo for signin (dark background) and a colored logo for dashboard (light background).
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  onClick={() => handleLogoConfirmResponse(true)}
                  sx={{ flex: 1, bgcolor: '#1D9E75', color: '#fff', py: 1.5, textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#0F6E56' } }}
                >
                  Same Logo
                </Button>
                <Button
                  onClick={() => handleLogoConfirmResponse(false)}
                  variant="outlined"
                  sx={{ flex: 1, borderColor: '#1D9E75', color: '#1D9E75', py: 1.5, textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#E1F5EE' } }}
                >
                  Different Logos
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {/* Logo & Favicon */}
        <Box sx={{ bgcolor: '#fff', border: '1px solid rgba(0,0,0,.08)', borderRadius: 2.5, mb: 3 }}>
          <Box sx={{ p: 3, borderBottom: '1px solid rgba(0,0,0,.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: config.chipBg, display: 'flex', alignItems: 'center', justifyContent: 'center', '& svg': { width: 20, height: 20, stroke: config.color } }}>
                {Icons.branding}
              </Box>
              <Box>
                <Typography sx={{ fontSize: 17, fontWeight: 600, color: '#151918' }}>Logo & Favicon</Typography>
                <Typography sx={{ fontSize: 13, color: '#8A9A94', mt: 0.3 }}>Displayed in the app header and browser tab</Typography>
              </Box>
            </Box>
            {type !== 'client' && (
              <FormControlLabel
                control={
                  <Switch
                    checked={useSeparateSigninLogo}
                    onChange={(e) => setUseSeparateSigninLogo(e.target.checked)}
                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#1D9E75' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#1D9E75' } }}
                  />
                }
                label="Separate signin logo"
                labelPlacement="start"
                sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: 13, color: '#4A5550' } }}
              />
            )}
          </Box>
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: (useSeparateSigninLogo && type !== 'client') ? '1fr 1fr 1fr' : '1fr 1fr', gap: 3 }}>
              {/* Dashboard Logo */}
              <Box>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#4A5550', mb: 1 }}>
                  {(useSeparateSigninLogo && type !== 'client') ? 'Dashboard Logo' : 'Logo'}
                  {type !== 'client' && <Typography component="span" sx={{ fontSize: 12, color: '#8A9A94', ml: 1 }}>(colored)</Typography>}
                </Typography>
                <Box
                  onClick={() => logoInputRef.current?.click()}
                  sx={{
                    border: '2px dashed rgba(0,0,0,.13)',
                    borderRadius: 2,
                    p: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    bgcolor: '#F2F4F3',
                    minHeight: 140,
                    transition: 'all .15s',
                    '&:hover': { borderColor: '#1D9E75', bgcolor: '#E1F5EE' },
                  }}
                >
                  {formData.logoUrl ? (
                    <img src={formData.logoUrl} alt="Logo" style={{ width: 80, height: 80, objectFit: 'contain', borderRadius: 8 }} />
                  ) : (
                    <>
                      <Box sx={{ width: 50, height: 50, borderRadius: 1.5, bgcolor: '#fff', border: '1px solid rgba(0,0,0,.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5, '& svg': { width: 24, height: 24 } }}>
                        {Icons.branding}
                      </Box>
                      <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#4A5550' }}>Upload logo</Typography>
                      <Typography sx={{ fontSize: 13, color: '#8A9A94', mt: 0.5 }}>PNG / SVG · 512×512</Typography>
                    </>
                  )}
                </Box>
                <input ref={logoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => { if (useSeparateSigninLogo || type === 'client') uploadFile(e.target.files?.[0], 'logo'); else handleFileUpload(e, 'logo'); }} />
              </Box>

              {/* Signin Logo - only shown when useSeparateSigninLogo is true AND not client type */}
              {useSeparateSigninLogo && type !== 'client' && (
                <Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#4A5550', mb: 1 }}>
                    Signin Logo
                    <Typography component="span" sx={{ fontSize: 12, color: '#8A9A94', ml: 1 }}>(white/light)</Typography>
                  </Typography>
                  <Box
                    onClick={() => signinLogoInputRef.current?.click()}
                    sx={{
                      border: '2px dashed rgba(0,0,0,.13)',
                      borderRadius: 2,
                      p: 4,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      bgcolor: '#2a4a4e', // Dark background to preview white logo
                      minHeight: 140,
                      transition: 'all .15s',
                      '&:hover': { borderColor: '#1D9E75' },
                    }}
                  >
                    {formData.signinLogoUrl ? (
                      <img src={formData.signinLogoUrl} alt="Signin Logo" style={{ width: 80, height: 80, objectFit: 'contain', borderRadius: 8 }} />
                    ) : (
                      <>
                        <Box sx={{ width: 50, height: 50, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5, '& svg': { width: 24, height: 24, stroke: '#fff' } }}>
                          {Icons.branding}
                        </Box>
                        <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#fff' }}>Upload signin logo</Typography>
                        <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', mt: 0.5 }}>White/light version</Typography>
                      </>
                    )}
                  </Box>
                  <input ref={signinLogoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => uploadFile(e.target.files?.[0], 'signinLogo')} />
                </Box>
              )}

              {/* Favicon */}
              <Box>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#4A5550', mb: 1 }}>Favicon</Typography>
                <Box
                  onClick={() => faviconInputRef.current?.click()}
                  sx={{
                    border: '2px dashed rgba(0,0,0,.13)',
                    borderRadius: 2,
                    p: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    bgcolor: '#F2F4F3',
                    minHeight: 140,
                    transition: 'all .15s',
                    '&:hover': { borderColor: '#1D9E75', bgcolor: '#E1F5EE' },
                  }}
                >
                  {formData.faviconUrl ? (
                    <img src={formData.faviconUrl} alt="Favicon" style={{ width: 48, height: 48, objectFit: 'contain' }} />
                  ) : (
                    <>
                      <Box sx={{ width: 50, height: 50, borderRadius: 1.5, bgcolor: '#fff', border: '1px solid rgba(0,0,0,.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5, '& svg': { width: 24, height: 24 } }}>
                        {Icons.domain}
                      </Box>
                      <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#4A5550' }}>Upload favicon</Typography>
                      <Typography sx={{ fontSize: 13, color: '#8A9A94', mt: 0.5 }}>ICO / PNG · 32×32</Typography>
                    </>
                  )}
                </Box>
                <input ref={faviconInputRef} type="file" accept="image/*,.ico" style={{ display: 'none' }} onChange={(e) => handleFileUpload(e, 'favicon')} />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Brand Color - Per Account Type */}
        <Box sx={{ bgcolor: '#fff', border: '1px solid rgba(0,0,0,.08)', borderRadius: 2.5, overflow: 'hidden', mb: 3 }}>
          <Box sx={{ p: 3, borderBottom: '1px solid rgba(0,0,0,.08)', display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: '#F2F4F3', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: formData.accountBranding?.[type]?.primaryColor || config.color }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 17, fontWeight: 600, color: '#151918' }}>Brand colours for {config.fullName}</Typography>
              <Typography sx={{ fontSize: 13, color: '#8A9A94', mt: 0.3 }}>Applied to buttons, links and highlights in this app only</Typography>
            </Box>
          </Box>
          <Box sx={{ p: 3 }}>
            {/* Primary Color */}
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#4A5550', mb: 1 }}>Primary colour</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 1.5,
                  bgcolor: formData.accountBranding?.[type]?.primaryColor || config.color,
                  cursor: 'pointer',
                  border: '2px solid rgba(0,0,0,.13)',
                  transition: 'transform .1s',
                  '&:hover': { transform: 'scale(1.05)' },
                }}
                onClick={() => document.getElementById(`color-primary-${type}`).click()}
              />
              <input
                type="color"
                id={`color-primary-${type}`}
                value={formData.accountBranding?.[type]?.primaryColor || config.color}
                onChange={(e) => setFormData((prev) => ({
                  ...prev,
                  accountBranding: {
                    ...prev.accountBranding,
                    [type]: { ...prev.accountBranding?.[type], primaryColor: e.target.value }
                  }
                }))}
                style={{ display: 'none' }}
              />
              <TextField
                size="small"
                value={formData.accountBranding?.[type]?.primaryColor || ''}
                placeholder={config.color}
                onChange={(e) => {
                  if (/^#[0-9A-Fa-f]{0,6}$/.test(e.target.value) || e.target.value === '') {
                    setFormData((prev) => ({
                      ...prev,
                      accountBranding: {
                        ...prev.accountBranding,
                        [type]: { ...prev.accountBranding?.[type], primaryColor: e.target.value }
                      }
                    }));
                  }
                }}
                sx={{ width: 120, '& input': { fontSize: 14, fontFamily: 'monospace', p: 1.2 } }}
              />
              <Typography sx={{ fontSize: 13, color: '#8A9A94' }}>Buttons & interactive elements</Typography>
            </Box>

            {/* Secondary Color */}
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#4A5550', mb: 1 }}>Secondary colour</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 1.5,
                  bgcolor: formData.accountBranding?.[type]?.secondaryColor || config.fg,
                  cursor: 'pointer',
                  border: '2px solid rgba(0,0,0,.13)',
                  transition: 'transform .1s',
                  '&:hover': { transform: 'scale(1.05)' },
                }}
                onClick={() => document.getElementById(`color-secondary-${type}`).click()}
              />
              <input
                type="color"
                id={`color-secondary-${type}`}
                value={formData.accountBranding?.[type]?.secondaryColor || config.fg}
                onChange={(e) => setFormData((prev) => ({
                  ...prev,
                  accountBranding: {
                    ...prev.accountBranding,
                    [type]: { ...prev.accountBranding?.[type], secondaryColor: e.target.value }
                  }
                }))}
                style={{ display: 'none' }}
              />
              <TextField
                size="small"
                value={formData.accountBranding?.[type]?.secondaryColor || ''}
                placeholder={config.fg}
                onChange={(e) => {
                  if (/^#[0-9A-Fa-f]{0,6}$/.test(e.target.value) || e.target.value === '') {
                    setFormData((prev) => ({
                      ...prev,
                      accountBranding: {
                        ...prev.accountBranding,
                        [type]: { ...prev.accountBranding?.[type], secondaryColor: e.target.value }
                      }
                    }));
                  }
                }}
                sx={{ width: 120, '& input': { fontSize: 14, fontFamily: 'monospace', p: 1.2 } }}
              />
              <Typography sx={{ fontSize: 13, color: '#8A9A94' }}>Links & accents</Typography>
            </Box>

            {/* Button Hover Color */}
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#4A5550', mb: 1 }}>Button hover colour</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 1.5,
                  bgcolor: formData.accountBranding?.[type]?.buttonHoverColor || formData.accountBranding?.[type]?.secondaryColor || config.fg,
                  cursor: 'pointer',
                  border: '2px solid rgba(0,0,0,.13)',
                  transition: 'transform .1s',
                  '&:hover': { transform: 'scale(1.05)' },
                }}
                onClick={() => document.getElementById(`color-hover-${type}`).click()}
              />
              <input
                type="color"
                id={`color-hover-${type}`}
                value={formData.accountBranding?.[type]?.buttonHoverColor || formData.accountBranding?.[type]?.secondaryColor || config.fg}
                onChange={(e) => setFormData((prev) => ({
                  ...prev,
                  accountBranding: {
                    ...prev.accountBranding,
                    [type]: { ...prev.accountBranding?.[type], buttonHoverColor: e.target.value }
                  }
                }))}
                style={{ display: 'none' }}
              />
              <TextField
                size="small"
                value={formData.accountBranding?.[type]?.buttonHoverColor || ''}
                placeholder={formData.accountBranding?.[type]?.secondaryColor || config.fg}
                onChange={(e) => {
                  if (e.target.value === '' || /^#[0-9A-Fa-f]{0,6}$/.test(e.target.value)) {
                    setFormData((prev) => ({
                      ...prev,
                      accountBranding: {
                        ...prev.accountBranding,
                        [type]: { ...prev.accountBranding?.[type], buttonHoverColor: e.target.value }
                      }
                    }));
                  }
                }}
                sx={{ width: 120, '& input': { fontSize: 14, fontFamily: 'monospace', p: 1.2 } }}
              />
              <Typography sx={{ fontSize: 13, color: '#8A9A94' }}>Button hover state</Typography>
            </Box>

            {/* Button Box Shadow */}
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#4A5550', mb: 1 }}>Button box shadow</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <TextField
                size="small"
                fullWidth
                value={formData.accountBranding?.[type]?.buttonBoxShadow || ''}
                placeholder="0 4px 15px rgba(0,0,0,0.2)"
                onChange={(e) => setFormData((prev) => ({
                  ...prev,
                  accountBranding: {
                    ...prev.accountBranding,
                    [type]: { ...prev.accountBranding?.[type], buttonBoxShadow: e.target.value }
                  }
                }))}
                sx={{ maxWidth: 350, '& input': { fontSize: 14, fontFamily: 'monospace', p: 1.2 } }}
              />
              <Typography sx={{ fontSize: 13, color: '#8A9A94' }}>CSS box-shadow value</Typography>
            </Box>

            {/* Preview */}
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#4A5550', mb: 1, mt: 3 }}>Live preview</Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', p: 2.5, bgcolor: '#F2F4F3', borderRadius: 1.5 }}>
              <Button
                sx={{
                  bgcolor: formData.accountBranding?.[type]?.primaryColor || config.color,
                  color: '#fff',
                  fontSize: 14,
                  textTransform: 'none',
                  px: 2.5,
                  py: 1,
                  boxShadow: formData.accountBranding?.[type]?.buttonBoxShadow || 'none',
                  '&:hover': {
                    bgcolor: formData.accountBranding?.[type]?.buttonHoverColor || formData.accountBranding?.[type]?.secondaryColor || config.fg,
                    boxShadow: formData.accountBranding?.[type]?.buttonBoxShadow || 'none',
                  }
                }}
              >
                Button (Hover me)
              </Button>
              <Typography sx={{ color: formData.accountBranding?.[type]?.primaryColor || config.color, fontSize: 15, fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}>Text link</Typography>
              <Box sx={{ px: 1.5, py: 0.6, borderRadius: 20, bgcolor: `${formData.accountBranding?.[type]?.primaryColor || config.color}22`, color: formData.accountBranding?.[type]?.primaryColor || config.color, fontSize: 13, fontWeight: 700 }}>
                Badge
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Follow-Up QR Code - Only for Follow-up type */}
        {type === 'followup' && (
          <Box sx={{ bgcolor: '#fff', border: '1px solid rgba(0,0,0,.08)', borderRadius: 2.5, overflow: 'hidden', mb: 3 }}>
            <Box sx={{ p: 3, borderBottom: '1px solid rgba(0,0,0,.08)', display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: '#F2F4F3', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
                📱
              </Box>
              <Box>
                <Typography sx={{ fontSize: 17, fontWeight: 600, color: '#151918' }}>Follow-Up QR Code</Typography>
                <Typography sx={{ fontSize: 13, color: '#8A9A94', mt: 0.3 }}>Upload the QR code image shown on the follow-up login page</Typography>
              </Box>
            </Box>
            <Box sx={{ p: 3 }}>
              {formData.accountBranding?.followup?.qrCodeUrl && (
                <Box sx={{ mb: 2, textAlign: 'center' }}>
                  <img
                    src={formData.accountBranding.followup.qrCodeUrl}
                    alt="Follow-up QR Code"
                    style={{ width: '150px', height: '150px', objectFit: 'contain', border: '2px solid #4a7a2c', borderRadius: '12px', padding: '8px' }}
                  />
                </Box>
              )}
              <input
                type="file"
                accept="image/*"
                id="followup-qr-upload"
                style={{ display: 'none' }}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  e.target.value = '';
                  // Upload as a file, the same way the logo and favicon do.
                  // Reading it into form state as base64 meant the QR was
                  // re-submitted on every Save and truncated by the input
                  // sanitizer, corrupting it even when nothing had changed.
                  await uploadBrandingImage(file, 'qrCode', 'followup');
                }}
              />
              <Button
                onClick={() => document.getElementById('followup-qr-upload').click()}
                disabled={uploading}
                sx={{
                  width: '100%',
                  py: 1.5,
                  border: '2px dashed #c2cad3',
                  borderRadius: 2,
                  color: '#4A5550',
                  textTransform: 'none',
                  fontSize: 14,
                  fontWeight: 500,
                  '&:hover': { borderColor: '#4a7a2c', bgcolor: '#f0f9f0' },
                }}
              >
                {formData.accountBranding?.followup?.qrCodeUrl ? 'Change QR Code Image' : 'Upload QR Code Image'}
              </Button>
            </Box>
          </Box>
        )}

        {/* SEO / Social Meta Tags - All app types */}
        <Box sx={{ bgcolor: '#fff', border: '1px solid rgba(0,0,0,.08)', borderRadius: 2.5, overflow: 'hidden', mb: 3 }}>
          <Box sx={{ p: 3, borderBottom: '1px solid rgba(0,0,0,.08)', display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: '#F2F4F3', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: 20 }}>🔗</span>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 17, fontWeight: 600, color: '#151918' }}>Link Preview / SEO</Typography>
              <Typography sx={{ fontSize: 13, color: '#8A9A94', mt: 0.3 }}>Controls how the link appears when shared on WhatsApp, Facebook, iMessage, etc.</Typography>
            </Box>
          </Box>
          <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#333', mb: 0.5 }}>Meta Title</Typography>
              <input
                type="text"
                value={formData.accountBranding?.[type]?.metaTitle || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  accountBranding: {
                    ...prev.accountBranding,
                    [type]: { ...prev.accountBranding?.[type], metaTitle: e.target.value }
                  }
                }))}
                placeholder={`e.g., Elite Pain & Health - ${type === 'followup' ? 'Follow-Up Form' : type === 'client' ? 'Patient Intake' : type.charAt(0).toUpperCase() + type.slice(1)}`}
                style={{ width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, fontFamily: 'inherit' }}
              />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#333', mb: 0.5 }}>Meta Description</Typography>
              <textarea
                value={formData.accountBranding?.[type]?.metaDescription || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  accountBranding: {
                    ...prev.accountBranding,
                    [type]: { ...prev.accountBranding?.[type], metaDescription: e.target.value }
                  }
                }))}
                placeholder="Short description shown in link previews (max 160 characters)"
                maxLength={160}
                rows={2}
                style={{ width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', resize: 'vertical' }}
              />
              <Typography sx={{ fontSize: 11, color: '#999', mt: 0.3 }}>{(formData.accountBranding?.[type]?.metaDescription || '').length}/160</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#333', mb: 0.5 }}>Meta Image (Link Preview Image)</Typography>
              <Typography sx={{ fontSize: 12, color: '#8A9A94', mb: 1 }}>Recommended: 1200×630px. This image shows when the link is shared on social media.</Typography>
              {formData.accountBranding?.[type]?.metaImage && (
                <Box sx={{ mb: 1.5, textAlign: 'center' }}>
                  <img
                    src={formData.accountBranding[type].metaImage}
                    alt="Meta preview"
                    style={{ maxWidth: 300, maxHeight: 160, objectFit: 'contain', borderRadius: 8, border: '1px solid #eee' }}
                  />
                </Box>
              )}
              <Button
                variant="outlined"
                component="label"
                size="small"
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                {formData.accountBranding?.[type]?.metaImage ? 'Change Image' : 'Upload Image'}
                <input
                  type="file"
                  hidden
                  accept="image/png,image/jpeg,image/webp"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    e.target.value = '';
                    // Uploaded as a file for the same reason as the QR code —
                    // holding it in form state meant every Save re-submitted
                    // the base64 and corrupted it.
                    await uploadBrandingImage(file, 'metaImage', type);
                  }}
                />
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Button Styles - Only for Admin */}
        {type === 'admin' && (
          <Box sx={{ bgcolor: '#fff', border: '1px solid rgba(0,0,0,.08)', borderRadius: 2.5, overflow: 'hidden', mb: 3 }}>
            <Box sx={{ p: 3, borderBottom: '1px solid rgba(0,0,0,.08)', display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: '#F2F4F3', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ width: 20, height: 10, borderRadius: 2, bgcolor: formData.accountBranding?.admin?.primaryColor || '#06AB8B' }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 17, fontWeight: 600, color: '#151918' }}>Button Styles</Typography>
                <Typography sx={{ fontSize: 13, color: '#8A9A94', mt: 0.3 }}>Customize individual button colors for Admin Dashboard</Typography>
              </Box>
            </Box>
            <Box sx={{ p: 3 }}>
              {/* Button style rows */}
              {[
                { key: 'addModule', label: 'Add New Module', defaultBg: '#1e1e1e', defaultHover: '#474747' },
                { key: 'import', label: 'Import', defaultBg: formData.accountBranding?.admin?.primaryColor || '#06AB8B', defaultHover: formData.accountBranding?.admin?.secondaryColor || '#049572' },
                { key: 'rename', label: 'Rename', defaultBg: formData.accountBranding?.admin?.primaryColor || '#06AB8B', defaultHover: formData.accountBranding?.admin?.secondaryColor || '#049572' },
                { key: 'delete', label: 'Delete', defaultBg: '#dc3545', defaultHover: '#c82333' },
                { key: 'export', label: 'Export', defaultBg: 'transparent', defaultHover: '#f0f0f0' },
                { key: 'save', label: 'Save', defaultBg: formData.accountBranding?.admin?.primaryColor || '#06AB8B', defaultHover: formData.accountBranding?.admin?.secondaryColor || '#049572' },
                { key: 'signin', label: 'Sign In Page', defaultBg: formData.accountBranding?.admin?.primaryColor || '#06AB8B', defaultHover: formData.accountBranding?.admin?.secondaryColor || '#049572' },
              ].map((btn) => (
                <Box key={btn.key} sx={{ mb: 3, pb: 3, borderBottom: '1px solid rgba(0,0,0,.05)' }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#4A5550', mb: 2 }}>{btn.label} Button</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                    {/* Background Color */}
                    <Box>
                      <Typography sx={{ fontSize: 12, color: '#8A9A94', mb: 0.5 }}>Background</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 1,
                            bgcolor: formData.accountBranding?.admin?.buttonStyles?.[btn.key]?.bg || btn.defaultBg,
                            border: '1px solid rgba(0,0,0,.13)',
                            cursor: 'pointer',
                          }}
                          onClick={() => document.getElementById(`btn-${btn.key}-bg`).click()}
                        />
                        <input
                          type="color"
                          id={`btn-${btn.key}-bg`}
                          value={formData.accountBranding?.admin?.buttonStyles?.[btn.key]?.bg || btn.defaultBg}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            accountBranding: {
                              ...prev.accountBranding,
                              admin: {
                                ...prev.accountBranding?.admin,
                                buttonStyles: {
                                  ...prev.accountBranding?.admin?.buttonStyles,
                                  [btn.key]: { ...prev.accountBranding?.admin?.buttonStyles?.[btn.key], bg: e.target.value }
                                }
                              }
                            }
                          }))}
                          style={{ display: 'none' }}
                        />
                        <TextField
                          size="small"
                          value={formData.accountBranding?.admin?.buttonStyles?.[btn.key]?.bg || ''}
                          placeholder={btn.defaultBg}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            accountBranding: {
                              ...prev.accountBranding,
                              admin: {
                                ...prev.accountBranding?.admin,
                                buttonStyles: {
                                  ...prev.accountBranding?.admin?.buttonStyles,
                                  [btn.key]: { ...prev.accountBranding?.admin?.buttonStyles?.[btn.key], bg: e.target.value }
                                }
                              }
                            }
                          }))}
                          sx={{ width: 100, '& input': { fontSize: 12, fontFamily: 'monospace', p: 0.8 } }}
                        />
                      </Box>
                    </Box>
                    {/* Text Color */}
                    <Box>
                      <Typography sx={{ fontSize: 12, color: '#8A9A94', mb: 0.5 }}>Text Color</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 1,
                            bgcolor: formData.accountBranding?.admin?.buttonStyles?.[btn.key]?.text || '#ffffff',
                            border: '1px solid rgba(0,0,0,.13)',
                            cursor: 'pointer',
                          }}
                          onClick={() => document.getElementById(`btn-${btn.key}-text`).click()}
                        />
                        <input
                          type="color"
                          id={`btn-${btn.key}-text`}
                          value={formData.accountBranding?.admin?.buttonStyles?.[btn.key]?.text || '#ffffff'}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            accountBranding: {
                              ...prev.accountBranding,
                              admin: {
                                ...prev.accountBranding?.admin,
                                buttonStyles: {
                                  ...prev.accountBranding?.admin?.buttonStyles,
                                  [btn.key]: { ...prev.accountBranding?.admin?.buttonStyles?.[btn.key], text: e.target.value }
                                }
                              }
                            }
                          }))}
                          style={{ display: 'none' }}
                        />
                        <TextField
                          size="small"
                          value={formData.accountBranding?.admin?.buttonStyles?.[btn.key]?.text || ''}
                          placeholder="#ffffff"
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            accountBranding: {
                              ...prev.accountBranding,
                              admin: {
                                ...prev.accountBranding?.admin,
                                buttonStyles: {
                                  ...prev.accountBranding?.admin?.buttonStyles,
                                  [btn.key]: { ...prev.accountBranding?.admin?.buttonStyles?.[btn.key], text: e.target.value }
                                }
                              }
                            }
                          }))}
                          sx={{ width: 100, '& input': { fontSize: 12, fontFamily: 'monospace', p: 0.8 } }}
                        />
                      </Box>
                    </Box>
                    {/* Hover Color */}
                    <Box>
                      <Typography sx={{ fontSize: 12, color: '#8A9A94', mb: 0.5 }}>Hover Background</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 1,
                            bgcolor: formData.accountBranding?.admin?.buttonStyles?.[btn.key]?.hover || btn.defaultHover,
                            border: '1px solid rgba(0,0,0,.13)',
                            cursor: 'pointer',
                          }}
                          onClick={() => document.getElementById(`btn-${btn.key}-hover`).click()}
                        />
                        <input
                          type="color"
                          id={`btn-${btn.key}-hover`}
                          value={formData.accountBranding?.admin?.buttonStyles?.[btn.key]?.hover || btn.defaultHover}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            accountBranding: {
                              ...prev.accountBranding,
                              admin: {
                                ...prev.accountBranding?.admin,
                                buttonStyles: {
                                  ...prev.accountBranding?.admin?.buttonStyles,
                                  [btn.key]: { ...prev.accountBranding?.admin?.buttonStyles?.[btn.key], hover: e.target.value }
                                }
                              }
                            }
                          }))}
                          style={{ display: 'none' }}
                        />
                        <TextField
                          size="small"
                          value={formData.accountBranding?.admin?.buttonStyles?.[btn.key]?.hover || ''}
                          placeholder={btn.defaultHover}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            accountBranding: {
                              ...prev.accountBranding,
                              admin: {
                                ...prev.accountBranding?.admin,
                                buttonStyles: {
                                  ...prev.accountBranding?.admin?.buttonStyles,
                                  [btn.key]: { ...prev.accountBranding?.admin?.buttonStyles?.[btn.key], hover: e.target.value }
                                }
                              }
                            }
                          }))}
                          sx={{ width: 100, '& input': { fontSize: 12, fontFamily: 'monospace', p: 0.8 } }}
                        />
                      </Box>
                    </Box>
                  </Box>
                  {/* Live Preview */}
                  <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Typography sx={{ fontSize: 12, color: '#8A9A94' }}>Preview:</Typography>
                    <Button
                      sx={{
                        bgcolor: formData.accountBranding?.admin?.buttonStyles?.[btn.key]?.bg || btn.defaultBg,
                        color: formData.accountBranding?.admin?.buttonStyles?.[btn.key]?.text || '#ffffff',
                        fontSize: 12,
                        textTransform: 'none',
                        px: 2,
                        py: 0.5,
                        minWidth: 'auto',
                        '&:hover': {
                          bgcolor: formData.accountBranding?.admin?.buttonStyles?.[btn.key]?.hover || btn.defaultHover,
                        }
                      }}
                    >
                      {btn.label}
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Button Styles - Only for SuperAdmin */}
        {type === 'superadmin' && (
          <Box sx={{ bgcolor: '#fff', border: '1px solid rgba(0,0,0,.08)', borderRadius: 2.5, overflow: 'hidden', mb: 3 }}>
            <Box sx={{ p: 3, borderBottom: '1px solid rgba(0,0,0,.08)', display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: '#F2F4F3', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ width: 20, height: 10, borderRadius: 2, bgcolor: formData.accountBranding?.superadmin?.primaryColor || '#EF9F27' }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 17, fontWeight: 600, color: '#151918' }}>Button Styles</Typography>
                <Typography sx={{ fontSize: 13, color: '#8A9A94', mt: 0.3 }}>Customize individual button colors for SuperAdmin Panel</Typography>
              </Box>
            </Box>
            <Box sx={{ p: 3 }}>
              {/* Button style rows */}
              {[
                { key: 'signin', label: 'Sign In Page', defaultBg: formData.accountBranding?.superadmin?.primaryColor || '#EF9F27', defaultHover: formData.accountBranding?.superadmin?.secondaryColor || '#633806' },
                { key: 'createAdmin', label: 'Create New Admin', defaultBg: formData.accountBranding?.superadmin?.primaryColor || '#EF9F27', defaultHover: formData.accountBranding?.superadmin?.secondaryColor || '#633806' },
                { key: 'editAdmin', label: 'Edit Admin', defaultBg: '#2196F3', defaultHover: '#1976D2' },
                { key: 'deleteAdmin', label: 'Delete Admin', defaultBg: '#f44336', defaultHover: '#d32f2f' },
              ].map((btn) => (
                <Box key={btn.key} sx={{ mb: 3, pb: 3, borderBottom: '1px solid rgba(0,0,0,.05)' }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#4A5550', mb: 2 }}>{btn.label} Button</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                    {/* Background Color */}
                    <Box>
                      <Typography sx={{ fontSize: 12, color: '#8A9A94', mb: 0.5 }}>Background</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 1,
                            bgcolor: formData.accountBranding?.superadmin?.buttonStyles?.[btn.key]?.bg || btn.defaultBg,
                            border: '1px solid rgba(0,0,0,.13)',
                            cursor: 'pointer',
                          }}
                          onClick={() => document.getElementById(`superadmin-btn-${btn.key}-bg`).click()}
                        />
                        <input
                          type="color"
                          id={`superadmin-btn-${btn.key}-bg`}
                          value={formData.accountBranding?.superadmin?.buttonStyles?.[btn.key]?.bg || btn.defaultBg}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            accountBranding: {
                              ...prev.accountBranding,
                              superadmin: {
                                ...prev.accountBranding?.superadmin,
                                buttonStyles: {
                                  ...prev.accountBranding?.superadmin?.buttonStyles,
                                  [btn.key]: { ...prev.accountBranding?.superadmin?.buttonStyles?.[btn.key], bg: e.target.value }
                                }
                              }
                            }
                          }))}
                          style={{ width: 0, height: 0, opacity: 0 }}
                        />
                        <TextField
                          size="small"
                          value={formData.accountBranding?.superadmin?.buttonStyles?.[btn.key]?.bg || ''}
                          placeholder={btn.defaultBg}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            accountBranding: {
                              ...prev.accountBranding,
                              superadmin: {
                                ...prev.accountBranding?.superadmin,
                                buttonStyles: {
                                  ...prev.accountBranding?.superadmin?.buttonStyles,
                                  [btn.key]: { ...prev.accountBranding?.superadmin?.buttonStyles?.[btn.key], bg: e.target.value }
                                }
                              }
                            }
                          }))}
                          sx={{ flex: 1, '& .MuiOutlinedInput-input': { py: 0.8, fontSize: 13 } }}
                        />
                      </Box>
                    </Box>
                    {/* Text Color */}
                    <Box>
                      <Typography sx={{ fontSize: 12, color: '#8A9A94', mb: 0.5 }}>Text Color</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 1,
                            bgcolor: formData.accountBranding?.superadmin?.buttonStyles?.[btn.key]?.text || '#ffffff',
                            border: '1px solid rgba(0,0,0,.13)',
                            cursor: 'pointer',
                          }}
                          onClick={() => document.getElementById(`superadmin-btn-${btn.key}-text`).click()}
                        />
                        <input
                          type="color"
                          id={`superadmin-btn-${btn.key}-text`}
                          value={formData.accountBranding?.superadmin?.buttonStyles?.[btn.key]?.text || '#ffffff'}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            accountBranding: {
                              ...prev.accountBranding,
                              superadmin: {
                                ...prev.accountBranding?.superadmin,
                                buttonStyles: {
                                  ...prev.accountBranding?.superadmin?.buttonStyles,
                                  [btn.key]: { ...prev.accountBranding?.superadmin?.buttonStyles?.[btn.key], text: e.target.value }
                                }
                              }
                            }
                          }))}
                          style={{ width: 0, height: 0, opacity: 0 }}
                        />
                        <TextField
                          size="small"
                          value={formData.accountBranding?.superadmin?.buttonStyles?.[btn.key]?.text || ''}
                          placeholder="#ffffff"
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            accountBranding: {
                              ...prev.accountBranding,
                              superadmin: {
                                ...prev.accountBranding?.superadmin,
                                buttonStyles: {
                                  ...prev.accountBranding?.superadmin?.buttonStyles,
                                  [btn.key]: { ...prev.accountBranding?.superadmin?.buttonStyles?.[btn.key], text: e.target.value }
                                }
                              }
                            }
                          }))}
                          sx={{ flex: 1, '& .MuiOutlinedInput-input': { py: 0.8, fontSize: 13 } }}
                        />
                      </Box>
                    </Box>
                    {/* Hover Color */}
                    <Box>
                      <Typography sx={{ fontSize: 12, color: '#8A9A94', mb: 0.5 }}>Hover</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 1,
                            bgcolor: formData.accountBranding?.superadmin?.buttonStyles?.[btn.key]?.hover || btn.defaultHover,
                            border: '1px solid rgba(0,0,0,.13)',
                            cursor: 'pointer',
                          }}
                          onClick={() => document.getElementById(`superadmin-btn-${btn.key}-hover`).click()}
                        />
                        <input
                          type="color"
                          id={`superadmin-btn-${btn.key}-hover`}
                          value={formData.accountBranding?.superadmin?.buttonStyles?.[btn.key]?.hover || btn.defaultHover}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            accountBranding: {
                              ...prev.accountBranding,
                              superadmin: {
                                ...prev.accountBranding?.superadmin,
                                buttonStyles: {
                                  ...prev.accountBranding?.superadmin?.buttonStyles,
                                  [btn.key]: { ...prev.accountBranding?.superadmin?.buttonStyles?.[btn.key], hover: e.target.value }
                                }
                              }
                            }
                          }))}
                          style={{ width: 0, height: 0, opacity: 0 }}
                        />
                        <TextField
                          size="small"
                          value={formData.accountBranding?.superadmin?.buttonStyles?.[btn.key]?.hover || ''}
                          placeholder={btn.defaultHover}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            accountBranding: {
                              ...prev.accountBranding,
                              superadmin: {
                                ...prev.accountBranding?.superadmin,
                                buttonStyles: {
                                  ...prev.accountBranding?.superadmin?.buttonStyles,
                                  [btn.key]: { ...prev.accountBranding?.superadmin?.buttonStyles?.[btn.key], hover: e.target.value }
                                }
                              }
                            }
                          }))}
                          sx={{ flex: 1, '& .MuiOutlinedInput-input': { py: 0.8, fontSize: 13 } }}
                        />
                      </Box>
                    </Box>
                  </Box>
                  {/* Live Preview */}
                  <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Typography sx={{ fontSize: 12, color: '#8A9A94' }}>Preview:</Typography>
                    <Button
                      sx={{
                        bgcolor: formData.accountBranding?.superadmin?.buttonStyles?.[btn.key]?.bg || btn.defaultBg,
                        color: formData.accountBranding?.superadmin?.buttonStyles?.[btn.key]?.text || '#ffffff',
                        fontSize: 12,
                        textTransform: 'none',
                        px: 2,
                        py: 0.5,
                        minWidth: 'auto',
                        '&:hover': {
                          bgcolor: formData.accountBranding?.superadmin?.buttonStyles?.[btn.key]?.hover || btn.defaultHover,
                        }
                      }}
                    >
                      {btn.label}
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Save Bar */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3 }}>
          <Button variant="outlined" sx={{ fontSize: 14, textTransform: 'none', borderColor: 'rgba(0,0,0,.13)', color: '#4A5550', px: 3, py: 1 }}>
            Discard
          </Button>
          <Button onClick={handleSave} disabled={saving} sx={{ bgcolor: '#1D9E75', color: '#fff', fontSize: 14, textTransform: 'none', px: 3, py: 1, '&:hover': { bgcolor: '#0F6E56' } }}>
            {saving ? 'Saving...' : 'Save changes'}
          </Button>
        </Box>
      </Box>
    );
  };

  const renderDomain = (type) => {
    const config = ACCOUNT_TYPES[type];
    const domainKey = `${type}Domain`;
    const domain = formData[domainKey];
    const verified = domainStatus[type]?.verified;
    const target = dnsTargets[type];
    const sevallaData = sevallaDnsRecords[type];

    const getSubdomain = (d) => {
      if (!d) return '';
      const parts = d.split('.');
      return parts.length > 2 ? parts[0] : '@';
    };

    // Helper to replace placeholder with actual subdomain
    const replaceSubdomainPlaceholder = (name) => {
      if (!name || name === '[subdomain]' || name === 'your-subdomain') {
        return getSubdomain(domain);
      }
      return name.replace('[subdomain]', getSubdomain(domain));
    };

    // Get DNS records to display (from Sevalla or default)
    // Always use local domain to calculate subdomain (override API placeholders)
    const rawRecords = sevallaData?.records || [{
      type: 'CNAME',
      name: getSubdomain(domain),
      value: target,
      purpose: 'origin',
      required: true,
    }];

    // Replace any placeholder names with actual subdomain from local domain input
    const dnsRecords = rawRecords.map(record => ({
      ...record,
      name: replaceSubdomainPlaceholder(record.name),
    }));

    return (
      <Box>
        <Box sx={{ mb: 4 }}>
          <Typography sx={{ fontSize: 28, fontWeight: 600, color: '#151918' }}>Custom Domain</Typography>
          <Typography sx={{ fontSize: 15, color: '#8A9A94', mt: 1 }}>Configure a custom domain for {config.fullName}.</Typography>
        </Box>

        {/* Shareable URL — correct meta tags for social sharing */}
        <Box sx={{ bgcolor: '#fff', border: '2px solid #22c55e', borderRadius: 2.5, p: 3, mb: 3 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.07em', color: '#22c55e', mb: 0.5 }}>
            Shareable URL (WhatsApp / Discord / Facebook / Link Previews)
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#8A9A94', mb: 1.5 }}>
            Share this link — shows correct title, description & image from branding on social media.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{ fontSize: 14, fontFamily: 'monospace', color: config.fg, bgcolor: config.bg, px: 2, py: 1, borderRadius: 1.5, wordBreak: 'break-all' }}>
              {environment.baseUrl}/share/{type}/{subaccount.slug}
            </Typography>
            <IconButton onClick={() => copyToClipboard(`${environment.baseUrl}/share/${type}/${subaccount.slug}`)}>
              {Icons.copy}
            </IconButton>
          </Box>
        </Box>

        {/* Default URL */}
        <Box sx={{ bgcolor: '#fff', border: '1px solid rgba(0,0,0,.08)', borderRadius: 2.5, p: 3, mb: 3 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.07em', color: '#8A9A94', mb: 0.5 }}>
            Direct URL
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#8A9A94', mb: 1.5 }}>
            Direct link to the application (meta tags load via JavaScript).
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {target ? (
              <>
                <Typography sx={{ fontSize: 14, fontFamily: 'monospace', color: config.fg, bgcolor: config.bg, px: 2, py: 1, borderRadius: 1.5 }}>
                  {target}?tenant={subaccount.slug}
                </Typography>
                <IconButton onClick={() => copyToClipboard(`https://${target}?tenant=${subaccount.slug}`)}>
                  {Icons.copy}
                </IconButton>
              </>
            ) : (
              <Typography sx={{ fontSize: 14, color: '#8A9A94', fontStyle: 'italic' }}>
                Loading Sevalla domain...
              </Typography>
            )}
          </Box>
        </Box>

        {/* Custom Domain Input */}
        <Box sx={{ bgcolor: '#fff', border: '1px solid rgba(0,0,0,.08)', borderRadius: 2.5, overflow: 'hidden', mb: 3 }}>
          <Box sx={{ p: 3, borderBottom: '1px solid rgba(0,0,0,.08)', display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: config.chipBg, display: 'flex', alignItems: 'center', justifyContent: 'center', '& svg': { width: 20, height: 20, stroke: config.color } }}>
              {Icons.domain}
            </Box>
            <Box>
              <Typography sx={{ fontSize: 17, fontWeight: 600, color: '#151918' }}>Custom Domain</Typography>
              <Typography sx={{ fontSize: 13, color: '#8A9A94', mt: 0.3 }}>Point your own domain to this application</Typography>
            </Box>
          </Box>
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid rgba(0,0,0,.08)', borderRadius: 1.5, overflow: 'hidden' }}>
              <Box sx={{ px: 2, py: 1.5, bgcolor: '#F2F4F3', color: '#8A9A94', fontSize: 14, fontFamily: 'monospace', borderRight: '1px solid rgba(0,0,0,.08)' }}>
                https://
              </Box>
              <TextField
                fullWidth
                placeholder={`${type}.yourdomain.com`}
                value={domain}
                onChange={(e) => {
                  const cleanValue = e.target.value.replace(/^https?:\/\//, '').replace(/\/+$/, '').toLowerCase().trim();
                  setFormData((prev) => ({ ...prev, [domainKey]: cleanValue }));
                }}
                sx={{ '& fieldset': { border: 'none' }, '& input': { fontSize: 15, p: 1.5 } }}
              />
              {verified && (
                <Box sx={{ px: 2, py: 0.8, bgcolor: '#22c55e', color: '#fff', fontSize: 13, fontWeight: 600, mr: 1.5, borderRadius: 1.5 }}>
                  Verified
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {/* DNS Configuration */}
        {domain && (
          <Box
            sx={{
              border: `2px solid ${verified ? '#22c55e' : '#f59e0b'}`,
              borderRadius: 2.5,
              overflow: 'hidden',
              mb: 3,
            }}
          >
            {/* Header */}
            <Box
              sx={{
                bgcolor: verified ? '#22c55e' : '#f59e0b',
                color: '#fff',
                px: 3,
                py: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {verified ? Icons.check : null}
                <Typography sx={{ fontSize: 18, fontWeight: 600 }}>
                  {verified ? 'Domain Connected!' : 'DNS Configuration Required'}
                </Typography>
                {verified && domainPrimaryStatus[type] && (
                  <Box sx={{ px: 1.5, py: 0.5, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.3)', fontSize: 12, fontWeight: 600 }}>
                    PRIMARY
                  </Box>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                {verified && !domainPrimaryStatus[type] && (
                  <Button
                    onClick={() => handleMakePrimary(type)}
                    disabled={makingPrimary[type]}
                    sx={{ bgcolor: '#fff', color: '#7c3aed', fontSize: 14, fontWeight: 600, textTransform: 'none', px: 3, py: 1, '&:hover': { bgcolor: '#f8fafc' } }}
                  >
                    {makingPrimary[type] ? 'Setting...' : 'Make Primary'}
                  </Button>
                )}
                <Button
                  onClick={() => handleRefreshDnsRecords(type)}
                  disabled={refreshingDns[type]}
                  sx={{ bgcolor: '#fff', color: '#3b82f6', fontSize: 14, fontWeight: 600, textTransform: 'none', px: 3, py: 1, '&:hover': { bgcolor: '#f8fafc' } }}
                >
                  {refreshingDns[type] ? 'Refreshing...' : 'Refresh DNS'}
                </Button>
                <Button
                  onClick={() => handleVerifyDomain(type)}
                  disabled={verifyingDomain[type]}
                  sx={{ bgcolor: '#fff', color: verified ? '#22c55e' : '#f59e0b', fontSize: 14, fontWeight: 600, textTransform: 'none', px: 3, py: 1, '&:hover': { bgcolor: '#f8fafc' } }}
                >
                  {verifyingDomain[type] ? 'Checking...' : verified ? 'Re-verify' : 'Verify Connection'}
                </Button>
              </Box>
            </Box>

            {/* DNS Records */}
            <Box sx={{ p: 3, bgcolor: '#fff' }}>
              {/* Sevalla status message */}
              {sevallaData?.sevallaMessage && (
                <Alert
                  severity={sevallaData?.fromSevalla ? 'success' : sevallaData?.sevallaAddResult?.success ? 'info' : 'warning'}
                  sx={{ mb: 2, fontSize: 13, '& .MuiAlert-message': { fontSize: 13 } }}
                >
                  {sevallaData.sevallaMessage}
                </Alert>
              )}
              {!verified && !sevallaData?.fromSevalla && !sevallaData?.sevallaMessage && (
                <Alert severity="info" sx={{ mb: 3, fontSize: 14, '& .MuiAlert-message': { fontSize: 14 } }}>
                  <strong>Note:</strong> If you just saved this domain, click "Refresh DNS" to get the A record and TXT record from Sevalla.
                </Alert>
              )}
              {!verified && (
                <Alert severity="warning" sx={{ mb: 3, fontSize: 14, '& .MuiAlert-message': { fontSize: 14 } }}>
                  <strong>Action Required:</strong> Add the following DNS record to connect your domain.
                </Alert>
              )}

              <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#334155', mb: 2 }}>
                Required DNS Record{dnsRecords.length > 1 ? 's' : ''}
                {sevallaData?.fromSevalla && (
                  <Typography component="span" sx={{ fontSize: 12, color: '#22c55e', ml: 1, fontWeight: 500 }}>
                    (from Sevalla)
                  </Typography>
                )}
              </Typography>

              {/* Display all DNS records */}
              {dnsRecords.map((record, idx) => (
                <Box key={idx} sx={{ mb: 2 }}>
                  {record.purpose === 'verification' && (
                    <Alert severity="info" sx={{ mb: 2, fontSize: 13 }}>
                      <strong>Domain Verification:</strong> Add this TXT record to verify domain ownership
                    </Alert>
                  )}
                  {record.purpose === 'ssl' && (
                    <Alert severity="warning" sx={{ mb: 2, fontSize: 13 }}>
                      <strong>SSL Certificate:</strong> Add this TXT record for HTTPS to work
                    </Alert>
                  )}
                  <Box sx={{ display: 'grid', gridTemplateColumns: '100px 1fr 1fr', gap: 2 }}>
                    {/* Type */}
                    <Box sx={{ p: 2.5, bgcolor: record.type === 'TXT' ? '#fef3c7' : '#f0fdf4', border: `1px solid ${record.type === 'TXT' ? '#fcd34d' : '#bbf7d0'}`, borderRadius: 1.5, textAlign: 'center' }}>
                      <Typography sx={{ fontSize: 12, color: record.type === 'TXT' ? '#92400e' : '#166534', fontWeight: 500 }}>TYPE</Typography>
                      <Typography sx={{ fontSize: 22, color: record.type === 'TXT' ? '#92400e' : '#166534', fontWeight: 700 }}>{record.type}</Typography>
                    </Box>

                    {/* Host */}
                    <Box sx={{ p: 2.5, bgcolor: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 1.5 }}>
                      <Typography sx={{ fontSize: 12, color: '#92400e', fontWeight: 500 }}>HOST / NAME</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
                        <Typography sx={{ fontSize: record.name?.length > 30 ? 12 : 16, fontFamily: 'monospace', fontWeight: 600, color: '#78350f', bgcolor: '#fff', px: 1.5, py: 0.8, borderRadius: 1, flex: 1, wordBreak: 'break-all' }}>
                          {record.name || getSubdomain(domain)}
                        </Typography>
                        <IconButton onClick={() => copyToClipboard(record.name || getSubdomain(domain))} size="small">
                          {Icons.copy}
                        </IconButton>
                      </Box>
                    </Box>

                    {/* Value */}
                    <Box sx={{ p: 2.5, bgcolor: '#e0f2fe', border: '1px solid #7dd3fc', borderRadius: 1.5 }}>
                      <Typography sx={{ fontSize: 12, color: '#0369a1', fontWeight: 500 }}>VALUE / POINTS TO</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
                        <Typography sx={{ fontSize: record.value?.length > 40 ? 11 : 13, fontFamily: 'monospace', fontWeight: 600, color: '#0c4a6e', bgcolor: '#fff', px: 1.5, py: 0.8, borderRadius: 1, flex: 1, wordBreak: 'break-all' }}>
                          {record.value || target}
                        </Typography>
                        <IconButton onClick={() => copyToClipboard(record.value || target)} size="small">
                          {Icons.copy}
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ))}

              {!verified && (
                <Box sx={{ bgcolor: '#f8fafc', borderRadius: 1.5, p: 3 }}>
                  <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#334155', mb: 2 }}>Quick Setup Steps</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {[
                      'Go to your domain provider (GoDaddy, Cloudflare, Namecheap) and open DNS Settings',
                      `Delete any existing A or CNAME record for "${getSubdomain(domain)}"`,
                      'Add a new CNAME record with the values shown above',
                      'Wait 5-30 minutes, then click "Verify Connection"',
                    ].map((step, i) => (
                      <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                        <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: config.fg, color: '#fff', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          {i + 1}
                        </Box>
                        <Typography sx={{ fontSize: 14, color: '#475569', pt: 0.3 }}>{step}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Save Bar */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3 }}>
          <Button onClick={handleSave} disabled={saving} sx={{ bgcolor: '#1D9E75', color: '#fff', fontSize: 14, textTransform: 'none', px: 3, py: 1, '&:hover': { bgcolor: '#0F6E56' } }}>
            {saving ? 'Saving...' : 'Save changes'}
          </Button>
        </Box>
      </Box>
    );
  };

  const renderPermissions = (type) => {
    const config = ACCOUNT_TYPES[type];
    const permissions = {
      client: [
        { key: 'pain_diagram', label: 'Pain Diagram', desc: 'Enable pain diagram feature for patients to mark areas of discomfort', on: formData.features.pain_diagram },
        { key: 'pdf_generation', label: 'PDF Generation', desc: 'Generate comprehensive PDF reports from form submissions', on: formData.features.pdf_generation },
      ],
      admin: [
        { key: 'pdf_generation', label: 'PDF Generation', desc: 'Generate comprehensive PDF reports from form submissions', on: formData.features.pdf_generation },
        { key: 'google_drive_upload', label: 'Google Drive Upload', desc: 'Automatically upload completed forms to Google Drive', on: formData.features.google_drive_upload },
      ],
      superadmin: [
        { key: 'pdf_generation', label: 'PDF Generation', desc: 'Generate comprehensive PDF reports from form submissions', on: formData.features.pdf_generation },
        { key: 'google_drive_upload', label: 'Google Drive Upload', desc: 'Automatically upload completed forms to Google Drive', on: formData.features.google_drive_upload },
      ],
      followup: [
        { key: 'followup_enabled', label: 'Follow-up System', desc: 'Enable automated follow-up reminders and notifications', on: formData.features.followup_enabled },
        { key: 'pdf_generation', label: 'PDF Generation', desc: 'Generate comprehensive PDF reports from form submissions', on: formData.features.pdf_generation },
      ],
    };

    return (
      <Box>
        <Box sx={{ mb: 4 }}>
          <Typography sx={{ fontSize: 28, fontWeight: 600, color: '#151918' }}>Permissions</Typography>
          <Typography sx={{ fontSize: 15, color: '#8A9A94', mt: 1 }}>Control what features are enabled in the {config.fullName}.</Typography>
        </Box>

        <Box sx={{ bgcolor: '#fff', border: '1px solid rgba(0,0,0,.08)', borderRadius: 2.5, overflow: 'hidden' }}>
          <Box sx={{ p: 3, borderBottom: '1px solid rgba(0,0,0,.08)', display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: config.chipBg, display: 'flex', alignItems: 'center', justifyContent: 'center', '& svg': { width: 20, height: 20, stroke: config.color } }}>
              {Icons.permissions}
            </Box>
            <Box>
              <Typography sx={{ fontSize: 17, fontWeight: 600, color: '#151918' }}>Feature toggles</Typography>
              <Typography sx={{ fontSize: 13, color: '#8A9A94', mt: 0.3 }}>Changes take effect immediately after saving</Typography>
            </Box>
          </Box>
          <Box sx={{ p: 0 }}>
            {permissions[type].map((perm, i) => (
              <Box
                key={perm.key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 3,
                  borderBottom: i < permissions[type].length - 1 ? '1px solid rgba(0,0,0,.08)' : 'none',
                  transition: 'background .15s',
                  '&:hover': { bgcolor: '#F8FAF9' },
                }}
              >
                <Box sx={{ flex: 1, pr: 3 }}>
                  <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#151918' }}>{perm.label}</Typography>
                  <Typography sx={{ fontSize: 14, color: '#8A9A94', mt: 0.5, lineHeight: 1.5 }}>{perm.desc}</Typography>
                </Box>
                <Switch
                  checked={perm.on}
                  onChange={() => {
                    setFormData((prev) => ({
                      ...prev,
                      features: { ...prev.features, [perm.key]: !prev.features[perm.key] },
                    }));
                  }}
                  sx={{
                    transform: 'scale(1.2)',
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#1D9E75' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#1D9E75' },
                  }}
                />
              </Box>
            ))}
          </Box>
        </Box>

        {/* Save Bar */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3 }}>
          <Button variant="outlined" sx={{ fontSize: 14, textTransform: 'none', borderColor: 'rgba(0,0,0,.13)', color: '#4A5550', px: 3, py: 1 }}>
            Discard
          </Button>
          <Button onClick={handleSave} disabled={saving} sx={{ bgcolor: '#1D9E75', color: '#fff', fontSize: 14, textTransform: 'none', px: 3, py: 1, '&:hover': { bgcolor: '#0F6E56' } }}>
            {saving ? 'Saving...' : 'Save changes'}
          </Button>
        </Box>
      </Box>
    );
  };

  const renderWebhooks = (type) => {
    const config = ACCOUNT_TYPES[type];

    // Different webhook configs for different account types
    const intakeWebhookConfig = [
      { key: 'step1', label: 'Patient Onboarding Details', desc: 'Triggered after Step 1 form submission' },
      { key: 'step4', label: 'Family History', desc: 'Triggered after Step 4 form submission' },
      { key: 'final', label: 'Final Submit', desc: 'Triggered after final form submission' },
    ];

    const followupWebhookConfig = [
      { key: 'followup_step1', label: 'Follow-Up First Step', desc: 'Triggered after follow-up form first step (module selection)' },
      { key: 'followup_submit', label: 'Follow-Up Submit', desc: 'Triggered when follow-up form is submitted with all module data, pain diagram, and complete details' },
    ];

    // Show follow-up webhooks for followup type, otherwise show intake webhooks
    const webhookConfig = type === 'followup' ? followupWebhookConfig : intakeWebhookConfig;
    const sectionTitle = type === 'followup' ? 'Follow-Up Webhooks' : 'Intake Form Webhooks';

    return (
      <Box>
        <Box sx={{ mb: 4 }}>
          <Typography sx={{ fontSize: 28, fontWeight: 600, color: '#151918' }}>{sectionTitle}</Typography>
          <Typography sx={{ fontSize: 15, color: '#8A9A94', mt: 1 }}>
            {type === 'followup'
              ? 'Configure webhook URLs for follow-up form submissions.'
              : `Configure webhook URLs for intake form submissions in ${config.fullName}.`}
          </Typography>
        </Box>

        <Box sx={{ bgcolor: '#fff', border: '1px solid rgba(0,0,0,.08)', borderRadius: 2.5, overflow: 'hidden', mb: 3 }}>
          <Box sx={{ p: 3, borderBottom: '1px solid rgba(0,0,0,.08)', display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: config.chipBg, display: 'flex', alignItems: 'center', justifyContent: 'center', '& svg': { width: 20, height: 20, stroke: config.color } }}>
              {Icons.webhook}
            </Box>
            <Box>
              <Typography sx={{ fontSize: 17, fontWeight: 600, color: '#151918' }}>
                {type === 'followup' ? 'Follow-Up Webhook' : 'Webhook Configuration'}
              </Typography>
              <Typography sx={{ fontSize: 13, color: '#8A9A94', mt: 0.3 }}>
                {type === 'followup' ? 'Set webhook URL for follow-up submissions' : 'Set webhook URLs for different form steps'}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ p: 0 }}>
            {webhookConfig.map((webhook, i) => (
              <Box
                key={webhook.key}
                sx={{
                  p: 3,
                  borderBottom: i < webhookConfig.length - 1 ? '1px solid rgba(0,0,0,.08)' : 'none',
                }}
              >
                <Box sx={{ mb: 1.5 }}>
                  <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#151918' }}>{webhook.label}</Typography>
                  <Typography sx={{ fontSize: 13, color: '#8A9A94', mt: 0.3 }}>{webhook.desc}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <TextField
                    fullWidth
                    size="small"
                    name={webhook.key}
                    value={webhooks[webhook.key] || ''}
                    onChange={handleWebhookChange}
                    placeholder="https://your-webhook-url.com/endpoint"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: 14,
                        fontFamily: 'monospace',
                        '& fieldset': { borderColor: 'rgba(0,0,0,.13)' },
                        '&:hover fieldset': { borderColor: 'rgba(0,0,0,.25)' },
                        '&.Mui-focused fieldset': { borderColor: '#1D9E75' },
                      },
                    }}
                  />
                  <Button
                    onClick={() => handleWebhookUpdate(webhook.key)}
                    disabled={webhookLoading}
                    sx={{
                      bgcolor: '#1D9E75',
                      color: '#fff',
                      fontSize: 14,
                      fontWeight: 600,
                      textTransform: 'none',
                      px: 3,
                      whiteSpace: 'nowrap',
                      '&:hover': { bgcolor: '#0F6E56' },
                      '&:disabled': { bgcolor: '#ccc' },
                    }}
                  >
                    {webhookLoading ? 'Saving...' : 'Save'}
                  </Button>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {webhookMessage && (
          <Alert
            severity={webhookMessage.includes('Error') ? 'error' : 'success'}
            sx={{ mb: 3 }}
          >
            {webhookMessage}
          </Alert>
        )}

        <Box sx={{ bgcolor: '#fffbeb', border: '1px solid #fde68a', borderRadius: 2, p: 2.5 }}>
          <Typography sx={{ fontSize: 14, color: '#92400e' }}>
            <strong>Note:</strong> Leave webhook URL empty to use global/default webhook settings.
            All URLs should be valid HTTP/HTTPS endpoints that accept POST requests.
          </Typography>
        </Box>
      </Box>
    );
  };

  // Render Google Drive configuration (only for Client)
  const renderGoogleDrive = (type) => {
    const typeInfo = ACCOUNT_TYPES[type];

    return (
      <Box>
        <Typography sx={{ fontSize: 28, fontWeight: 700, color: '#151918', mb: 1 }}>
          Google Drive Configuration
        </Typography>
        <Typography sx={{ fontSize: 15, color: '#4A5550', mb: 4 }}>
          Configure Google Drive integration for {typeInfo.fullName}. Each subaccount can have its own Google Drive folder and service account credentials.
        </Typography>

        {/* Current Status */}
        <Box sx={{ bgcolor: '#fff', borderRadius: 3, border: '1px solid rgba(0,0,0,.08)', p: 4, mb: 4 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#151918', mb: 3 }}>
            Current Configuration
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#8A9A94', mb: 1 }}>
                Status
              </Typography>
              <Box sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 0.75,
                borderRadius: 20,
                fontSize: 13,
                fontWeight: 600,
                bgcolor: googleDriveConfig.hasCredentials ? '#dcfce7' : '#fef3c7',
                color: googleDriveConfig.hasCredentials ? '#166534' : '#92400e',
              }}>
                {googleDriveConfig.hasCredentials ? 'Configured' : 'Not Configured'}
              </Box>
            </Box>

            {googleDriveConfig.hasCredentials && (
              <>
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#8A9A94', mb: 1 }}>
                    Project ID
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: '#151918', fontFamily: 'monospace' }}>
                    {googleDriveConfig.projectId || 'N/A'}
                  </Typography>
                </Box>
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#8A9A94', mb: 1 }}>
                    Service Account Email
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: '#151918', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {googleDriveConfig.clientEmail || 'N/A'}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Box>

        {/* Configuration Form */}
        <Box sx={{ bgcolor: '#fff', borderRadius: 3, border: '1px solid rgba(0,0,0,.08)', p: 4, mb: 4 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#151918', mb: 3 }}>
            Update Configuration
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Folder ID */}
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#151918', mb: 1 }}>
                Google Drive Folder ID <span style={{ color: '#dc2626' }}>*</span>
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g., 1AbCdEfGhIjKlMnOpQrStUvWxYz"
                value={googleDriveConfig.folderId}
                onChange={(e) => setGoogleDriveConfig(prev => ({ ...prev, folderId: e.target.value }))}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    fontFamily: 'monospace',
                    fontSize: 14,
                  }
                }}
              />
              <Typography sx={{ fontSize: 12, color: '#8A9A94', mt: 0.5 }}>
                The ID of the Google Drive folder where files will be uploaded. You can find this in the folder's URL.
              </Typography>
            </Box>

            {/* JSON File Upload */}
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#151918', mb: 1 }}>
                Service Account JSON File {!googleDriveConfig.hasCredentials && <span style={{ color: '#dc2626' }}>*</span>}
              </Typography>
              <input
                type="file"
                ref={googleDriveJsonRef}
                accept=".json,application/json"
                style={{ display: 'none' }}
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    setGoogleDriveUploading(true);
                    setTimeout(() => setGoogleDriveUploading(false), 500);
                  }
                }}
              />
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  onClick={() => googleDriveJsonRef.current?.click()}
                  startIcon={Icons.upload}
                  sx={{
                    borderColor: '#e5e7eb',
                    color: '#374151',
                    textTransform: 'none',
                    borderRadius: 2,
                    '&:hover': { borderColor: '#d1d5db', bgcolor: '#f9fafb' },
                  }}
                >
                  {googleDriveUploading ? 'Loading...' : 'Choose JSON File'}
                </Button>
                <Typography sx={{ fontSize: 13, color: '#8A9A94' }}>
                  {googleDriveJsonRef.current?.files?.[0]?.name || (googleDriveConfig.hasCredentials ? 'Credentials already uploaded' : 'No file selected')}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 12, color: '#8A9A94', mt: 0.5 }}>
                Upload the service account JSON file from Google Cloud Console. This file contains the credentials needed to access Google Drive.
              </Typography>
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
            <Button
              variant="contained"
              onClick={handleGoogleDriveUpdate}
              disabled={googleDriveLoading || !googleDriveConfig.folderId}
              sx={{
                bgcolor: typeInfo.color,
                color: '#fff',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                px: 3,
                '&:hover': { bgcolor: typeInfo.fg },
                '&:disabled': { bgcolor: '#e5e7eb', color: '#9ca3af' },
              }}
            >
              {googleDriveLoading ? 'Saving...' : 'Save Configuration'}
            </Button>

            {googleDriveConfig.hasCredentials && (
              <Button
                variant="outlined"
                onClick={handleGoogleDriveDelete}
                disabled={googleDriveLoading}
                startIcon={Icons.trash}
                sx={{
                  borderColor: '#fca5a5',
                  color: '#dc2626',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  '&:hover': { borderColor: '#f87171', bgcolor: '#fef2f2' },
                }}
              >
                Remove Configuration
              </Button>
            )}
          </Box>
        </Box>

        {/* Help Info */}
        <Box sx={{ bgcolor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 2, p: 2.5 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1e40af', mb: 1 }}>
            How to set up Google Drive integration:
          </Typography>
          <Box component="ol" sx={{ fontSize: 13, color: '#1e40af', pl: 2.5, m: 0, '& li': { mb: 0.5 } }}>
            <li>Go to Google Cloud Console and create a new project (or use existing)</li>
            <li>Enable the Google Drive API for your project</li>
            <li>Create a Service Account and download the JSON credentials file</li>
            <li>Create a folder in Google Drive and share it with the service account email</li>
            <li>Copy the folder ID from the URL and paste it above</li>
            <li>Upload the JSON credentials file</li>
          </Box>
        </Box>
      </Box>
    );
  };

  // Render content based on navigation
  const renderContent = () => {
    if (!currentType) return renderMainOverview();
    if (currentPage === 'overview') return renderTypeOverview(currentType);
    if (currentPage === 'branding') return renderBranding(currentType);
    if (currentPage === 'domain') return renderDomain(currentType);
    if (currentPage === 'permissions') return renderPermissions(currentType);
    if (currentPage === 'webhooks') return renderWebhooks(currentType);
    if (currentPage === 'googledrive') return renderGoogleDrive(currentType);
    return renderTypeOverview(currentType);
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#F2F4F3' }}>
      {/* Sidebar */}
      <Box
        sx={{
          width: 280,
          bgcolor: '#fff',
          borderRight: '1px solid rgba(0,0,0,.08)',
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
          overflow: 'auto',
        }}
      >
        {/* Header */}
        <Box sx={{ p: 3, borderBottom: '1px solid rgba(0,0,0,.08)' }}>
          <Box
            onClick={() => navigate('/subaccounts')}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 14, color: '#8A9A94', cursor: 'pointer', mb: 3, '&:hover': { color: '#151918' } }}
          >
            {Icons.back}
            All subaccounts
          </Box>
          <Box sx={{ width: 56, height: 56, borderRadius: 2, bgcolor: '#E1F5EE', border: '1px solid rgba(29,158,117,.22)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
            {formData.logoUrl ? (
              <img src={formData.logoUrl} alt="" style={{ width: 32, height: 32, objectFit: 'contain' }} />
            ) : (
              <Typography sx={{ fontSize: 20, fontWeight: 600, color: '#1D9E75' }}>{subaccount.name?.charAt(0)}</Typography>
            )}
          </Box>
          <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#151918' }}>{subaccount.name}</Typography>
          <Typography sx={{ fontSize: 13, color: '#8A9A94', fontFamily: 'monospace', mt: 0.5 }}>{subaccount.slug}</Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
            <Box sx={{ px: 1.5, py: 0.5, borderRadius: 20, fontSize: 12, fontWeight: 600, bgcolor: '#1D9E75', color: '#fff' }}>Active</Box>
          </Box>
        </Box>

        {/* Navigation */}
        <Box sx={{ p: 2 }}>
          {/* General */}
          <Typography sx={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.07em', color: '#8A9A94', px: 1.5, mb: 1 }}>
            General
          </Typography>
          <Box
            onClick={() => navigateTo(null, 'overview')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 1.5,
              py: 1.2,
              borderRadius: 1.5,
              fontSize: 15,
              fontWeight: !currentType ? 600 : 500,
              color: !currentType ? '#151918' : '#4A5550',
              bgcolor: !currentType ? '#F2F4F3' : 'transparent',
              cursor: 'pointer',
              position: 'relative',
              '&:hover': { bgcolor: '#F2F4F3' },
              '&::before': !currentType ? { content: '""', position: 'absolute', left: 0, top: '20%', bottom: '20%', width: 4, borderRadius: 2, bgcolor: '#1D9E75' } : {},
            }}
          >
            {Icons.grid}
            Overview
          </Box>

          {/* Account Types */}
          <Typography sx={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.07em', color: '#8A9A94', px: 1.5, mb: 1, mt: 3 }}>
            Account types
          </Typography>

          {Object.entries(ACCOUNT_TYPES).map(([key, type]) => (
            <Box key={key} sx={{ mb: 1 }}>
              {/* Type Header */}
              <Box
                onClick={() => toggleSection(key)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 1.5,
                  py: 1.2,
                  borderRadius: 1.5,
                  cursor: 'pointer',
                  bgcolor: openSections.has(key) ? '#F2F4F3' : 'transparent',
                  '&:hover': { bgcolor: '#F2F4F3' },
                }}
              >
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: type.color }} />
                <Typography sx={{ flex: 1, fontSize: 15, fontWeight: currentType === key ? 600 : 500, color: currentType === key ? '#151918' : '#4A5550' }}>
                  {type.name}
                </Typography>
                <Box sx={{ transform: openSections.has(key) ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}>{Icons.chevron}</Box>
              </Box>

              {/* Sub Items */}
              {openSections.has(key) && (
                <Box sx={{ pl: 3, mt: 0.5 }}>
                  {['overview', 'branding', 'domain', 'permissions', 'webhooks', 'googledrive']
                    .filter(page => {
                      // Permissions only for client
                      if (page === 'permissions') return key === 'client';
                      // Google Drive only for client
                      if (page === 'googledrive') return key === 'client';
                      return true;
                    })
                    .map((page) => (
                    <Box
                      key={page}
                      onClick={() => navigateTo(key, page)}
                      sx={{
                        px: 1.5,
                        py: 1,
                        borderRadius: 1,
                        fontSize: 14,
                        fontWeight: currentType === key && currentPage === page ? 600 : 500,
                        color: currentType === key && currentPage === page ? '#151918' : '#4A5550',
                        bgcolor: currentType === key && currentPage === page ? '#F2F4F3' : 'transparent',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: '#F2F4F3' },
                      }}
                    >
                      {page === 'googledrive' ? 'Google Drive' : page.charAt(0).toUpperCase() + page.slice(1)}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', bgcolor: '#F2F4F3' }}>
        <Box sx={{ p: 5, maxWidth: 960 }}>{renderContent()}</Box>
      </Box>
    </Box>
  );
};

export default EditSubaccount;
